import { z } from 'zod';
import { format } from './format';
export { EMPTY_PARAM } from './format';
import {
  type GlobalQuery,
  globalQuery,
  globalWire,
  lazy,
  month,
  oneOf,
  queryValue,
  segment
} from './query';
import type { Param } from './types';

// A declared route's query is two schemas. `<route>Query` builds what a caller
// may pass, and its input is the helper's type. `<route>Wire` is what runs: its
// `.transform()` writes the wire's names for the domain's, the global ones
// included, and passes on a key the declaration does not name as written,
// spread last so that one written under the wire's name is not replaced by the
// `undefined` of the camelCase key it left out. Its input admits any key, so
// the type cannot come from `<route>Wire` itself. A route that renames no key of
// its own runs `globalWire`.
//
// A route with no declaration reads no query parameter of its own, so it takes
// `GlobalQuery`, the ones every route does, through `globalWire`.
//
// A route whose path names a segment in more than one word has
// `<route>Segments`, which takes each segment's camelCase and writes the name
// Rails gives it. It requires each key and checks no value: `format` refuses a
// value it cannot use, by the name Rails gives the segment.
//
// Each schema is built the first time its helper runs rather than when this
// module is imported: every page loads it, and calls few of its helpers.

export function aboutPath(queryParams: GlobalQuery = {}): string {
  return format('/about', {}, globalWire.parse(queryParams ?? {}), {
    translate: true,
    prependLocale: true
  });
}

export function aboutContactDetailsPath(queryParams: GlobalQuery = {}): string {
  return format(
    '/about/contact-details',
    {},
    globalWire.parse(queryParams ?? {}),
    { translate: true, prependLocale: true }
  );
}

const acceptUserInvitationQuery = () =>
  z.object({
    ...globalQuery,
    invitationToken: queryValue
  });

export type AcceptUserInvitationQuery = z.input<
  ReturnType<typeof acceptUserInvitationQuery>
>;

const acceptUserInvitationWire = lazy(() =>
  acceptUserInvitationQuery()
    .catchall(queryValue)
    .transform(({ invitationToken, disableFeatures, ...query }) => ({
      invitation_token: invitationToken,
      disable_features: disableFeatures,
      ...query
    }))
);

export function acceptUserInvitationPath(
  queryParams: AcceptUserInvitationQuery = {}
): string {
  return format(
    '/invitation/accept',
    {},
    acceptUserInvitationWire().parse(queryParams ?? {}),
    { translate: true, prependLocale: true }
  );
}

const adminArchiveCancelledQuery = () =>
  z.object({
    ...globalQuery,
    organizationId: queryValue,
    page: queryValue
  });

export type AdminArchiveCancelledQuery = z.input<
  ReturnType<typeof adminArchiveCancelledQuery>
>;

const adminArchiveCancelledWire = lazy(() =>
  adminArchiveCancelledQuery()
    .catchall(queryValue)
    .transform(({ organizationId, disableFeatures, ...query }) => ({
      organization_id: organizationId,
      disable_features: disableFeatures,
      ...query
    }))
);

export function adminArchiveCancelledPath(
  queryParams: AdminArchiveCancelledQuery = {}
): string {
  return format(
    '/admin/archive/cancelled',
    {},
    adminArchiveCancelledWire().parse(queryParams ?? {}),
    { translate: true, prependLocale: true }
  );
}

const adminArchiveSucceededQuery = () =>
  z.object({
    ...globalQuery,
    organizationId: queryValue,
    page: queryValue
  });

export type AdminArchiveSucceededQuery = z.input<
  ReturnType<typeof adminArchiveSucceededQuery>
>;

const adminArchiveSucceededWire = lazy(() =>
  adminArchiveSucceededQuery()
    .catchall(queryValue)
    .transform(({ organizationId, disableFeatures, ...query }) => ({
      organization_id: organizationId,
      disable_features: disableFeatures,
      ...query
    }))
);

export function adminArchiveSucceededPath(
  queryParams: AdminArchiveSucceededQuery = {}
): string {
  return format(
    '/admin/archive',
    {},
    adminArchiveSucceededWire().parse(queryParams ?? {}),
    { translate: true, prependLocale: true }
  );
}

const adminArchiveUnknownQuery = () =>
  z.object({
    ...globalQuery,
    organizationId: queryValue,
    page: queryValue
  });

export type AdminArchiveUnknownQuery = z.input<
  ReturnType<typeof adminArchiveUnknownQuery>
>;

const adminArchiveUnknownWire = lazy(() =>
  adminArchiveUnknownQuery()
    .catchall(queryValue)
    .transform(({ organizationId, disableFeatures, ...query }) => ({
      organization_id: organizationId,
      disable_features: disableFeatures,
      ...query
    }))
);

export function adminArchiveUnknownPath(
  queryParams: AdminArchiveUnknownQuery = {}
): string {
  return format(
    '/admin/archive/unknown-author',
    {},
    adminArchiveUnknownWire().parse(queryParams ?? {}),
    { translate: true, prependLocale: true }
  );
}

export function adminCateringInquiryPath(
  pathParams: { id: Param },
  queryParams: GlobalQuery = {}
): string {
  return format(
    '/admin/inquiries/c-:id',
    pathParams,
    globalWire.parse(queryParams ?? {}),
    { translate: true, prependLocale: true }
  );
}

const adminCateringInquiryMessagesSegments = lazy(() =>
  z
    .object({ cateringInquiryId: segment })
    .transform(({ cateringInquiryId }) => ({
      catering_inquiry_id: cateringInquiryId
    }))
);

export type AdminCateringInquiryMessagesSegments = z.input<
  ReturnType<typeof adminCateringInquiryMessagesSegments>
>;

export function adminCateringInquiryMessagesPath(
  pathParams: AdminCateringInquiryMessagesSegments,
  queryParams: GlobalQuery = {}
): string {
  return format(
    '/admin/catering_inquiries/:catering_inquiry_id/messages',
    adminCateringInquiryMessagesSegments().parse(pathParams),
    globalWire.parse(queryParams ?? {}),
    { translate: true, prependLocale: true }
  );
}

const adminCommissionsQuery = () =>
  z.object({
    ...globalQuery,
    activeCateringProfileIds: queryValue,
    activeVenueIds: queryValue,
    displayAllCases: queryValue,
    eventAtInYearMonth: month,
    offsetByDays: queryValue,
    organizationId: queryValue,
    page: queryValue
  });

export type AdminCommissionsQuery = z.input<
  ReturnType<typeof adminCommissionsQuery>
>;

const adminCommissionsWire = lazy(() =>
  adminCommissionsQuery()
    .catchall(queryValue)
    .transform(
      ({
        displayAllCases,
        eventAtInYearMonth,
        offsetByDays,
        organizationId,
        disableFeatures,
        ...query
      }) => ({
        display_all_cases: displayAllCases,
        event_at_in_year_month: eventAtInYearMonth,
        offset_by_days: offsetByDays,
        organization_id: organizationId,
        disable_features: disableFeatures,
        ...query
      })
    )
);

export function adminCommissionsPath(
  queryParams: AdminCommissionsQuery = {}
): string {
  return format(
    '/admin/commissions',
    {},
    adminCommissionsWire().parse(queryParams ?? {}),
    { translate: true, prependLocale: true }
  );
}

const adminEventRequestAnomalyReportsSegments = lazy(() =>
  z
    .object({ eventRequestId: segment })
    .transform(({ eventRequestId }) => ({ event_request_id: eventRequestId }))
);

export type AdminEventRequestAnomalyReportsSegments = z.input<
  ReturnType<typeof adminEventRequestAnomalyReportsSegments>
>;

export function adminEventRequestAnomalyReportsPath(
  pathParams: AdminEventRequestAnomalyReportsSegments,
  queryParams: GlobalQuery = {}
): string {
  return format(
    '/admin/inquiries/:event_request_id/anomaly_reports',
    adminEventRequestAnomalyReportsSegments().parse(pathParams),
    globalWire.parse(queryParams ?? {}),
    { translate: true, prependLocale: true }
  );
}

const adminExportArchiveQuery = () =>
  z.object({
    ...globalQuery,
    organizationId: queryValue
  });

export type AdminExportArchiveQuery = z.input<
  ReturnType<typeof adminExportArchiveQuery>
>;

const adminExportArchiveWire = lazy(() =>
  adminExportArchiveQuery()
    .catchall(queryValue)
    .transform(({ organizationId, disableFeatures, ...query }) => ({
      organization_id: organizationId,
      disable_features: disableFeatures,
      ...query
    }))
);

export function adminExportArchivePath(
  queryParams: AdminExportArchiveQuery = {}
): string {
  return format(
    '/admin/archive/export',
    {},
    adminExportArchiveWire().parse(queryParams ?? {}),
    { translate: true, prependLocale: true }
  );
}

const adminInquiriesQuery = () =>
  z.object({
    ...globalQuery,
    order: oneOf<'last_message_at' | 'inquiry_submitted_at' | 'event_at'>(),
    page: queryValue,
    profiles: queryValue,
    search: queryValue,
    status: oneOf<'active' | 'all'>(),
    venues: queryValue
  });

export type AdminInquiriesQuery = z.input<
  ReturnType<typeof adminInquiriesQuery>
>;

export function adminInquiriesPath(
  queryParams: AdminInquiriesQuery = {}
): string {
  return format('/admin/inquiries', {}, globalWire.parse(queryParams ?? {}), {
    translate: true,
    prependLocale: true
  });
}

const adminInquiryRecipientsQuery = () =>
  z.object({
    ...globalQuery,
    organizationId: queryValue
  });

export type AdminInquiryRecipientsQuery = z.input<
  ReturnType<typeof adminInquiryRecipientsQuery>
>;

const adminInquiryRecipientsWire = lazy(() =>
  adminInquiryRecipientsQuery()
    .catchall(queryValue)
    .transform(({ organizationId, disableFeatures, ...query }) => ({
      organization_id: organizationId,
      disable_features: disableFeatures,
      ...query
    }))
);

export function adminInquiryRecipientsPath(
  queryParams: AdminInquiryRecipientsQuery = {}
): string {
  return format(
    '/admin/inquiry_recipients',
    {},
    adminInquiryRecipientsWire().parse(queryParams ?? {}),
    { translate: true, prependLocale: true }
  );
}

const adminMessageTemplatesQuery = () =>
  z.object({
    ...globalQuery,
    organizationId: queryValue
  });

export type AdminMessageTemplatesQuery = z.input<
  ReturnType<typeof adminMessageTemplatesQuery>
>;

const adminMessageTemplatesWire = lazy(() =>
  adminMessageTemplatesQuery()
    .catchall(queryValue)
    .transform(({ organizationId, disableFeatures, ...query }) => ({
      organization_id: organizationId,
      disable_features: disableFeatures,
      ...query
    }))
);

export function adminMessageTemplatesPath(
  queryParams: AdminMessageTemplatesQuery = {}
): string {
  return format(
    '/admin/message_templates',
    {},
    adminMessageTemplatesWire().parse(queryParams ?? {}),
    { translate: true, prependLocale: true }
  );
}

export function adminOrganizationPath(queryParams: GlobalQuery = {}): string {
  return format(
    '/admin/organization',
    {},
    globalWire.parse(queryParams ?? {}),
    { translate: true, prependLocale: true }
  );
}

const adminOrganizationInvoicesQuery = () =>
  z.object({
    ...globalQuery,
    organizationId: queryValue
  });

export type AdminOrganizationInvoicesQuery = z.input<
  ReturnType<typeof adminOrganizationInvoicesQuery>
>;

const adminOrganizationInvoicesWire = lazy(() =>
  adminOrganizationInvoicesQuery()
    .catchall(queryValue)
    .transform(({ organizationId, disableFeatures, ...query }) => ({
      organization_id: organizationId,
      disable_features: disableFeatures,
      ...query
    }))
);

export function adminOrganizationInvoicesPath(
  queryParams: AdminOrganizationInvoicesQuery = {}
): string {
  return format(
    '/admin/organization/invoices',
    {},
    adminOrganizationInvoicesWire().parse(queryParams ?? {}),
    { translate: true, prependLocale: true }
  );
}

export function adminPriceRequestPath(
  pathParams: { id: Param },
  queryParams: GlobalQuery = {}
): string {
  return format(
    '/admin/inquiries/:id',
    pathParams,
    globalWire.parse(queryParams ?? {}),
    { translate: true, prependLocale: true }
  );
}

const adminPriceRequestMessagesSegments = lazy(() =>
  z
    .object({ priceRequestId: segment })
    .transform(({ priceRequestId }) => ({ price_request_id: priceRequestId }))
);

export type AdminPriceRequestMessagesSegments = z.input<
  ReturnType<typeof adminPriceRequestMessagesSegments>
>;

export function adminPriceRequestMessagesPath(
  pathParams: AdminPriceRequestMessagesSegments,
  queryParams: GlobalQuery = {}
): string {
  return format(
    '/admin/inquiries/:price_request_id/messages',
    adminPriceRequestMessagesSegments().parse(pathParams),
    globalWire.parse(queryParams ?? {}),
    { translate: true, prependLocale: true }
  );
}

export function apiCommissionsConversationPreviewPath(
  queryParams: GlobalQuery = {}
): string {
  return format(
    '/api/commissions/conversation_preview',
    {},
    globalWire.parse(queryParams ?? {}),
    { translate: false, prependLocale: false }
  );
}

export function apiCommissionsInquiryCommandsPath(
  queryParams: GlobalQuery = {}
): string {
  return format(
    '/api/commissions/inquiry_commands',
    {},
    globalWire.parse(queryParams ?? {}),
    { translate: false, prependLocale: false }
  );
}

export function apiConversationsAppCommandsPath(
  queryParams: GlobalQuery = {}
): string {
  return format(
    '/api/conversations_app/commands',
    {},
    globalWire.parse(queryParams ?? {}),
    { translate: false, prependLocale: false }
  );
}

const apiConversationsAppCustomerEmailPermissionQuery = () =>
  z.object({
    ...globalQuery,
    externalId: queryValue
  });

export type APIConversationsAppCustomerEmailPermissionQuery = z.input<
  ReturnType<typeof apiConversationsAppCustomerEmailPermissionQuery>
>;

const apiConversationsAppCustomerEmailPermissionWire = lazy(() =>
  apiConversationsAppCustomerEmailPermissionQuery()
    .catchall(queryValue)
    .transform(({ externalId, disableFeatures, ...query }) => ({
      external_id: externalId,
      disable_features: disableFeatures,
      ...query
    }))
);

export function apiConversationsAppCustomerEmailPermissionPath(
  queryParams: APIConversationsAppCustomerEmailPermissionQuery = {}
): string {
  return format(
    '/api/conversations_app/customer_email_permission',
    {},
    apiConversationsAppCustomerEmailPermissionWire().parse(queryParams ?? {}),
    { translate: false, prependLocale: false }
  );
}

export function apiConversationsAppEventOutcomePath(
  queryParams: GlobalQuery = {}
): string {
  return format(
    '/api/conversations_app/event_outcome',
    {},
    globalWire.parse(queryParams ?? {}),
    { translate: false, prependLocale: false }
  );
}

const apiConversationsAppScrollFeedQuery = () =>
  z.object({
    ...globalQuery,
    scrollCursor: queryValue,
    scrollLimit: queryValue
  });

export type APIConversationsAppScrollFeedQuery = z.input<
  ReturnType<typeof apiConversationsAppScrollFeedQuery>
>;

const apiConversationsAppScrollFeedWire = lazy(() =>
  apiConversationsAppScrollFeedQuery()
    .catchall(queryValue)
    .transform(({ scrollCursor, scrollLimit, disableFeatures, ...query }) => ({
      'scroll-cursor': scrollCursor,
      'scroll-limit': scrollLimit,
      disable_features: disableFeatures,
      ...query
    }))
);

export function apiConversationsAppScrollFeedPath(
  pathParams: { collection: Param },
  queryParams: APIConversationsAppScrollFeedQuery = {}
): string {
  return format(
    '/api/conversations_app/scroll/feed/:collection',
    pathParams,
    apiConversationsAppScrollFeedWire().parse(queryParams ?? {}),
    { translate: false, prependLocale: false }
  );
}

const apiConversationsAppVenueProfileQuery = () =>
  z.object({
    ...globalQuery,
    externalId: queryValue,
    targetType: queryValue
  });

export type APIConversationsAppVenueProfileQuery = z.input<
  ReturnType<typeof apiConversationsAppVenueProfileQuery>
>;

const apiConversationsAppVenueProfileWire = lazy(() =>
  apiConversationsAppVenueProfileQuery()
    .catchall(queryValue)
    .transform(({ externalId, targetType, disableFeatures, ...query }) => ({
      external_id: externalId,
      target_type: targetType,
      disable_features: disableFeatures,
      ...query
    }))
);

export function apiConversationsAppVenueProfilePath(
  queryParams: APIConversationsAppVenueProfileQuery = {}
): string {
  return format(
    '/api/conversations_app/venue_profile',
    {},
    apiConversationsAppVenueProfileWire().parse(queryParams ?? {}),
    { translate: false, prependLocale: false }
  );
}

export function apiConversationsAppWebhooksPath(
  queryParams: GlobalQuery = {}
): string {
  return format(
    '/api/conversations_app/webhooks',
    {},
    globalWire.parse(queryParams ?? {}),
    { translate: false, prependLocale: false }
  );
}

export function apiV1ATMIndexPath(queryParams: GlobalQuery = {}): string {
  return format('/api/v1/atm', {}, globalWire.parse(queryParams ?? {}), {
    translate: false,
    prependLocale: false
  });
}

export function apiV1EventRequestPath(
  pathParams: { id: Param },
  queryParams: GlobalQuery = {}
): string {
  return format(
    '/api/v1/event_requests/:id',
    pathParams,
    globalWire.parse(queryParams ?? {}),
    { translate: false, prependLocale: false }
  );
}

export function apiV1ListsPath(queryParams: GlobalQuery = {}): string {
  return format('/api/v1/lists', {}, globalWire.parse(queryParams ?? {}), {
    translate: false,
    prependLocale: false
  });
}

export function apiV1PhoneNumberRequestsPath(
  queryParams: GlobalQuery = {}
): string {
  return format(
    '/api/v1/phone_number_requests',
    {},
    globalWire.parse(queryParams ?? {}),
    { translate: false, prependLocale: false }
  );
}

export function apiV3GroupingGroupPath(
  pathParams: { id: Param },
  queryParams: GlobalQuery = {}
): string {
  return format(
    '/api/v3/grouping/groups/:id',
    pathParams,
    globalWire.parse(queryParams ?? {}),
    { translate: false, prependLocale: false }
  );
}

export function apiV3GroupingGroupsPath(queryParams: GlobalQuery = {}): string {
  return format(
    '/api/v3/grouping/groups',
    {},
    globalWire.parse(queryParams ?? {}),
    { translate: false, prependLocale: false }
  );
}

export function apiV3MessageTemplatePath(
  pathParams: { id: Param },
  queryParams: GlobalQuery = {}
): string {
  return format(
    '/api/v3/message_templates/:id',
    pathParams,
    globalWire.parse(queryParams ?? {}),
    { translate: false, prependLocale: false }
  );
}

export function apiV3MessageTemplatesPath(
  queryParams: GlobalQuery = {}
): string {
  return format(
    '/api/v3/message_templates',
    {},
    globalWire.parse(queryParams ?? {}),
    { translate: false, prependLocale: false }
  );
}

const apiV3OrganizationPackagesPackageOrderSegments = lazy(() =>
  z
    .object({ organizationId: segment, packageType: segment })
    .transform(({ organizationId, packageType }) => ({
      organization_id: organizationId,
      package_type: packageType
    }))
);

export type APIV3OrganizationPackagesPackageOrderSegments = z.input<
  ReturnType<typeof apiV3OrganizationPackagesPackageOrderSegments>
>;

export function apiV3OrganizationPackagesPackageOrderPath(
  pathParams: APIV3OrganizationPackagesPackageOrderSegments,
  queryParams: GlobalQuery = {}
): string {
  return format(
    '/api/v3/organizations/:organization_id/packages/:package_type/order',
    apiV3OrganizationPackagesPackageOrderSegments().parse(pathParams),
    globalWire.parse(queryParams ?? {}),
    { translate: false, prependLocale: false }
  );
}

export function apiV3PackagesCelebrationPackagePath(
  pathParams: { id: Param },
  queryParams: GlobalQuery = {}
): string {
  return format(
    '/api/v3/packages/celebration_packages/:id',
    pathParams,
    globalWire.parse(queryParams ?? {}),
    { translate: false, prependLocale: false }
  );
}

export function apiV3PackagesCelebrationPackagesPath(
  queryParams: GlobalQuery = {}
): string {
  return format(
    '/api/v3/packages/celebration_packages',
    {},
    globalWire.parse(queryParams ?? {}),
    { translate: false, prependLocale: false }
  );
}

export function apiV3PackagesExperiencePackagePath(
  pathParams: { id: Param },
  queryParams: GlobalQuery = {}
): string {
  return format(
    '/api/v3/packages/experience_packages/:id',
    pathParams,
    globalWire.parse(queryParams ?? {}),
    { translate: false, prependLocale: false }
  );
}

export function apiV3PackagesExperiencePackagesPath(
  queryParams: GlobalQuery = {}
): string {
  return format(
    '/api/v3/packages/experience_packages',
    {},
    globalWire.parse(queryParams ?? {}),
    { translate: false, prependLocale: false }
  );
}

export function apiV3PackagesMeetingPackagePath(
  pathParams: { id: Param },
  queryParams: GlobalQuery = {}
): string {
  return format(
    '/api/v3/packages/meeting_packages/:id',
    pathParams,
    globalWire.parse(queryParams ?? {}),
    { translate: false, prependLocale: false }
  );
}

export function apiV3PackagesMeetingPackagesPath(
  queryParams: GlobalQuery = {}
): string {
  return format(
    '/api/v3/packages/meeting_packages',
    {},
    globalWire.parse(queryParams ?? {}),
    { translate: false, prependLocale: false }
  );
}

export function apiV3PackagesVenuePackagePath(
  pathParams: { id: Param },
  queryParams: GlobalQuery = {}
): string {
  return format(
    '/api/v3/packages/venue_packages/:id',
    pathParams,
    globalWire.parse(queryParams ?? {}),
    { translate: false, prependLocale: false }
  );
}

export function apiV3PackagesVenuePackagesPath(
  queryParams: GlobalQuery = {}
): string {
  return format(
    '/api/v3/packages/venue_packages',
    {},
    globalWire.parse(queryParams ?? {}),
    { translate: false, prependLocale: false }
  );
}

const autocompleteProfilesVenueProfilesQuery = () =>
  z.object({
    ...globalQuery,
    query: queryValue
  });

export type AutocompleteProfilesVenueProfilesQuery = z.input<
  ReturnType<typeof autocompleteProfilesVenueProfilesQuery>
>;

export function autocompleteProfilesVenueProfilesPath(
  queryParams: AutocompleteProfilesVenueProfilesQuery = {}
): string {
  return format(
    '/venues/autocomplete',
    {},
    globalWire.parse(queryParams ?? {}),
    { translate: true, prependLocale: true }
  );
}

const autocompleteStaffProspectsQuery = () =>
  z.object({
    ...globalQuery,
    query: queryValue
  });

export type AutocompleteStaffProspectsQuery = z.input<
  ReturnType<typeof autocompleteStaffProspectsQuery>
>;

export function autocompleteStaffProspectsPath(
  queryParams: AutocompleteStaffProspectsQuery = {}
): string {
  return format(
    '/staff/prospects/autocomplete',
    {},
    globalWire.parse(queryParams ?? {}),
    { translate: true, prependLocale: true }
  );
}

export function ballinPath(queryParams: GlobalQuery = {}): string {
  return format('/ballin', {}, globalWire.parse(queryParams ?? {}), {
    translate: true,
    prependLocale: true
  });
}

const batchStaffInvoicesQuery = () =>
  z.object({
    ...globalQuery,
    date: queryValue
  });

export type BatchStaffInvoicesQuery = z.input<
  ReturnType<typeof batchStaffInvoicesQuery>
>;

export function batchStaffInvoicesPath(
  queryParams: BatchStaffInvoicesQuery = {}
): string {
  return format(
    '/staff/invoices/batch',
    {},
    globalWire.parse(queryParams ?? {}),
    { translate: true, prependLocale: true }
  );
}

const billingAdminCommonBillingBillingDetailsQuery = () =>
  z.object({
    ...globalQuery,
    organizationId: queryValue
  });

export type BillingAdminCommonBillingBillingDetailsQuery = z.input<
  ReturnType<typeof billingAdminCommonBillingBillingDetailsQuery>
>;

const billingAdminCommonBillingBillingDetailsWire = lazy(() =>
  billingAdminCommonBillingBillingDetailsQuery()
    .catchall(queryValue)
    .transform(({ organizationId, disableFeatures, ...query }) => ({
      organization_id: organizationId,
      disable_features: disableFeatures,
      ...query
    }))
);

export function billingAdminCommonBillingBillingDetailsPath(
  queryParams: BillingAdminCommonBillingBillingDetailsQuery = {}
): string {
  return format(
    '/admin/billing_details',
    {},
    billingAdminCommonBillingBillingDetailsWire().parse(queryParams ?? {}),
    { translate: true, prependLocale: true }
  );
}

const billingAdminCommonBillingStripePortalRedirectQuery = () =>
  z.object({
    ...globalQuery,
    flowType: queryValue
  });

export type BillingAdminCommonBillingStripePortalRedirectQuery = z.input<
  ReturnType<typeof billingAdminCommonBillingStripePortalRedirectQuery>
>;

const billingAdminCommonBillingStripePortalRedirectWire = lazy(() =>
  billingAdminCommonBillingStripePortalRedirectQuery()
    .catchall(queryValue)
    .transform(({ flowType, disableFeatures, ...query }) => ({
      flow_type: flowType,
      disable_features: disableFeatures,
      ...query
    }))
);

export function billingAdminCommonBillingStripePortalRedirectPath(
  queryParams: BillingAdminCommonBillingStripePortalRedirectQuery = {}
): string {
  return format(
    '/admin/common_billing_stripe_portal_redirect',
    {},
    billingAdminCommonBillingStripePortalRedirectWire().parse(
      queryParams ?? {}
    ),
    { translate: true, prependLocale: true }
  );
}

export function billingAPICommonBillingStripeBillingDetailPath(
  pathParams: { id: Param },
  queryParams: GlobalQuery = {}
): string {
  return format(
    '/api/common_billing_stripe_billing_details/:id',
    pathParams,
    globalWire.parse(queryParams ?? {}),
    { translate: false, prependLocale: false }
  );
}

export function billingAPICommonBillingStripeCustomerPath(
  pathParams: { id: Param },
  queryParams: GlobalQuery = {}
): string {
  return format(
    '/api/common_billing_stripe_customers/:id',
    pathParams,
    globalWire.parse(queryParams ?? {}),
    { translate: false, prependLocale: false }
  );
}

export function billingAPICommonBillingStripeCustomersPath(
  queryParams: GlobalQuery = {}
): string {
  return format(
    '/api/common_billing_stripe_customers',
    {},
    globalWire.parse(queryParams ?? {}),
    { translate: false, prependLocale: false }
  );
}

const billingAPICommonBillingStripeDraftInvoicesQuery = () =>
  z.object({
    ...globalQuery,
    id: queryValue
  });

export type BillingAPICommonBillingStripeDraftInvoicesQuery = z.input<
  ReturnType<typeof billingAPICommonBillingStripeDraftInvoicesQuery>
>;

export function billingAPICommonBillingStripeDraftInvoicesPath(
  queryParams: BillingAPICommonBillingStripeDraftInvoicesQuery = {}
): string {
  return format(
    '/api/common_billing_stripe_draft_invoices',
    {},
    globalWire.parse(queryParams ?? {}),
    { translate: false, prependLocale: false }
  );
}

export function billingStaffCommonBillingStripeDailyInvoicePath(
  pathParams: { id: Param },
  queryParams: GlobalQuery = {}
): string {
  return format(
    '/staff/common_billing_stripe_daily_invoices/:id',
    pathParams,
    globalWire.parse(queryParams ?? {}),
    { translate: true, prependLocale: true }
  );
}

export function billingStaffCommonBillingStripeDailyInvoicesPath(
  queryParams: GlobalQuery = {}
): string {
  return format(
    '/staff/common_billing_stripe_daily_invoices',
    {},
    globalWire.parse(queryParams ?? {}),
    { translate: true, prependLocale: true }
  );
}

export function billingStaffCommonBillingStripeInvoicesPath(
  queryParams: GlobalQuery = {}
): string {
  return format(
    '/staff/common_billing_stripe_invoices',
    {},
    globalWire.parse(queryParams ?? {}),
    { translate: true, prependLocale: true }
  );
}

export function blogPostPath(
  pathParams: { id: Param },
  queryParams: GlobalQuery = {}
): string {
  return format('/blog/:id', pathParams, globalWire.parse(queryParams ?? {}), {
    translate: true,
    prependLocale: true
  });
}

export function blogPostsPath(queryParams: GlobalQuery = {}): string {
  return format('/blog', {}, globalWire.parse(queryParams ?? {}), {
    translate: true,
    prependLocale: true
  });
}

export function blogTagPath(
  pathParams: { id: Param },
  queryParams: GlobalQuery = {}
): string {
  return format(
    '/blog/hashtag/:id',
    pathParams,
    globalWire.parse(queryParams ?? {}),
    { translate: true, prependLocale: true }
  );
}

export function cancelUserRegistrationPath(
  queryParams: GlobalQuery = {}
): string {
  return format('/cancel', {}, globalWire.parse(queryParams ?? {}), {
    translate: true,
    prependLocale: true
  });
}

export function cateringConversationReplyEmailsPath(
  queryParams: GlobalQuery = {}
): string {
  return format(
    '/webhooks/catering_conversation_reply_emails',
    {},
    globalWire.parse(queryParams ?? {}),
    { translate: true, prependLocale: true }
  );
}

export function cateringLandingPagePath(
  pathParams: { id: Param },
  queryParams: GlobalQuery = {}
): string {
  return format('/:id', pathParams, globalWire.parse(queryParams ?? {}), {
    translate: true,
    prependLocale: true
  });
}

export function cateringListYourServicePath(
  queryParams: GlobalQuery = {}
): string {
  return format(
    '/catering/list-your-service',
    {},
    globalWire.parse(queryParams ?? {}),
    { translate: true, prependLocale: true }
  );
}

const cateringProfileInquiriesSegments = lazy(() =>
  z
    .object({ profileId: segment })
    .transform(({ profileId }) => ({ profile_id: profileId }))
);

export type CateringProfileInquiriesSegments = z.input<
  ReturnType<typeof cateringProfileInquiriesSegments>
>;

export function cateringProfileInquiriesPath(
  pathParams: CateringProfileInquiriesSegments,
  queryParams: GlobalQuery = {}
): string {
  return format(
    '/catering/:profile_id/inquiries',
    cateringProfileInquiriesSegments().parse(pathParams),
    globalWire.parse(queryParams ?? {}),
    { translate: true, prependLocale: true }
  );
}

const cateringProfileMenusSegments = lazy(() =>
  z
    .object({ profileId: segment })
    .transform(({ profileId }) => ({ profile_id: profileId }))
);

export type CateringProfileMenusSegments = z.input<
  ReturnType<typeof cateringProfileMenusSegments>
>;

export function cateringProfileMenusPath(
  pathParams: CateringProfileMenusSegments,
  queryParams: GlobalQuery = {}
): string {
  return format(
    '/catering/:profile_id/menus',
    cateringProfileMenusSegments().parse(pathParams),
    globalWire.parse(queryParams ?? {}),
    { translate: true, prependLocale: true }
  );
}

const cateringSearchQuery = () =>
  z.object({
    ...globalQuery,
    cuisine: queryValue,
    event: queryValue,
    location: queryValue,
    name: queryValue,
    page: queryValue,
    service: queryValue,
    serving: queryValue
  });

export type CateringSearchQuery = z.input<
  ReturnType<typeof cateringSearchQuery>
>;

export function cateringSearchPath(
  queryParams: CateringSearchQuery = {}
): string {
  return format('/catering', {}, globalWire.parse(queryParams ?? {}), {
    translate: true,
    prependLocale: true
  });
}

export function conversationReplyEmailsPath(
  queryParams: GlobalQuery = {}
): string {
  return format(
    '/webhooks/conversation_reply_emails',
    {},
    globalWire.parse(queryParams ?? {}),
    { translate: true, prependLocale: true }
  );
}

export function dashboardPath(queryParams: GlobalQuery = {}): string {
  return format('/dashboard', {}, globalWire.parse(queryParams ?? {}), {
    translate: true,
    prependLocale: true
  });
}

export function destroyUserSessionPath(queryParams: GlobalQuery = {}): string {
  return format('/logout', {}, globalWire.parse(queryParams ?? {}), {
    translate: true,
    prependLocale: true
  });
}

export function disableFeatureStaffOrganizationPath(
  pathParams: { id: Param },
  queryParams: GlobalQuery = {}
): string {
  return format(
    '/staff/organizations/:id/disable_feature',
    pathParams,
    globalWire.parse(queryParams ?? {}),
    { translate: true, prependLocale: true }
  );
}

export function downloadStaffSiteManagementLocationGeoShapesPath(
  queryParams: GlobalQuery = {}
): string {
  return format(
    '/staff/site_management/location_geo_shapes/download',
    {},
    globalWire.parse(queryParams ?? {}),
    { translate: true, prependLocale: true }
  );
}

export function duplicateListPath(
  pathParams: { id: Param },
  queryParams: GlobalQuery = {}
): string {
  return format(
    '/lists/:id/duplicate',
    pathParams,
    globalWire.parse(queryParams ?? {}),
    { translate: true, prependLocale: true }
  );
}

export function duplicateStaffLandingPagePath(
  pathParams: { id: Param },
  queryParams: GlobalQuery = {}
): string {
  return format(
    '/staff/landing_pages/:id/duplicate',
    pathParams,
    globalWire.parse(queryParams ?? {}),
    { translate: true, prependLocale: true }
  );
}

export function editAdminOrganizationPath(
  queryParams: GlobalQuery = {}
): string {
  return format(
    '/admin/organization/edit',
    {},
    globalWire.parse(queryParams ?? {}),
    { translate: true, prependLocale: true }
  );
}

const editAdminPackagesQuery = () =>
  z.object({
    ...globalQuery,
    organizationId: queryValue,
    packageType: oneOf<
      | 'celebration_packages'
      | 'experience_packages'
      | 'meeting_packages'
      | 'venue_packages'
    >(),
    shownVenueId: queryValue
  });

export type EditAdminPackagesQuery = z.input<
  ReturnType<typeof editAdminPackagesQuery>
>;

const editAdminPackagesWire = lazy(() =>
  editAdminPackagesQuery()
    .catchall(queryValue)
    .transform(
      ({
        organizationId,
        packageType,
        shownVenueId,
        disableFeatures,
        ...query
      }) => ({
        organization_id: organizationId,
        package_type: packageType,
        shown_venue_id: shownVenueId,
        disable_features: disableFeatures,
        ...query
      })
    )
);

export function editAdminPackagesPath(
  queryParams: EditAdminPackagesQuery = {}
): string {
  return format(
    '/admin/packages/edit',
    {},
    editAdminPackagesWire().parse(queryParams ?? {}),
    { translate: true, prependLocale: true }
  );
}

export function editGroupsPath(
  pathParams: { id: Param },
  queryParams: GlobalQuery = {}
): string {
  return format(
    '/venues/:id/groups/edit',
    pathParams,
    globalWire.parse(queryParams ?? {}),
    { translate: true, prependLocale: true }
  );
}

const editPackagesQuery = () =>
  z.object({
    ...globalQuery,
    packageType: oneOf<
      | 'celebration_packages'
      | 'experience_packages'
      | 'meeting_packages'
      | 'venue_packages'
    >(),
    shownVenueId: queryValue
  });

export type EditPackagesQuery = z.input<ReturnType<typeof editPackagesQuery>>;

const editPackagesWire = lazy(() =>
  editPackagesQuery()
    .catchall(queryValue)
    .transform(({ packageType, shownVenueId, disableFeatures, ...query }) => ({
      package_type: packageType,
      shown_venue_id: shownVenueId,
      disable_features: disableFeatures,
      ...query
    }))
);

export function editPackagesPath(
  pathParams: { id: Param },
  queryParams: EditPackagesQuery = {}
): string {
  return format(
    '/venues/:id/packages/edit',
    pathParams,
    editPackagesWire().parse(queryParams ?? {}),
    { translate: true, prependLocale: true }
  );
}

export function editProfilesCateringProfilePath(
  pathParams: { id: Param },
  queryParams: GlobalQuery = {}
): string {
  return format(
    '/catering/:id/edit',
    pathParams,
    globalWire.parse(queryParams ?? {}),
    { translate: true, prependLocale: true }
  );
}

export function editProfilesReviewPath(
  pathParams: { id: Param },
  queryParams: GlobalQuery = {}
): string {
  return format(
    '/reviews/:id/edit',
    pathParams,
    globalWire.parse(queryParams ?? {}),
    { translate: true, prependLocale: true }
  );
}

export function editProfilesSupplierProfilePath(
  pathParams: { id: Param },
  queryParams: GlobalQuery = {}
): string {
  return format(
    '/sp/:id/edit',
    pathParams,
    globalWire.parse(queryParams ?? {}),
    { translate: true, prependLocale: true }
  );
}

export function editProfilesVenueProfilePath(
  pathParams: { id: Param },
  queryParams: GlobalQuery = {}
): string {
  return format(
    '/venues/:id/edit',
    pathParams,
    globalWire.parse(queryParams ?? {}),
    { translate: true, prependLocale: true }
  );
}

export function editReviewPath(
  pathParams: { id: Param },
  queryParams: GlobalQuery = {}
): string {
  return format(
    '/reviews/:id/edit',
    pathParams,
    globalWire.parse(queryParams ?? {}),
    { translate: true, prependLocale: true }
  );
}

export function editStaffCateringLandingPagePath(
  pathParams: { id: Param },
  queryParams: GlobalQuery = {}
): string {
  return format(
    '/staff/catering_landing_pages/:id/edit',
    pathParams,
    globalWire.parse(queryParams ?? {}),
    { translate: true, prependLocale: true }
  );
}

export function editStaffCateringServiceAreaCityPath(
  pathParams: { id: Param },
  queryParams: GlobalQuery = {}
): string {
  return format(
    '/staff/catering/service_area_cities/:id/edit',
    pathParams,
    globalWire.parse(queryParams ?? {}),
    { translate: true, prependLocale: true }
  );
}

export function editStaffInvoicePath(
  pathParams: { id: Param },
  queryParams: GlobalQuery = {}
): string {
  return format(
    '/staff/invoices/:id/edit',
    pathParams,
    globalWire.parse(queryParams ?? {}),
    { translate: true, prependLocale: true }
  );
}

export function editStaffLandingPagePath(
  pathParams: { id: Param },
  queryParams: GlobalQuery = {}
): string {
  return format(
    '/staff/landing_pages/:id/edit',
    pathParams,
    globalWire.parse(queryParams ?? {}),
    { translate: true, prependLocale: true }
  );
}

const editStaffOrganizationQuery = () =>
  z.object({
    ...globalQuery,
    tab: oneOf<'basic' | 'profiles' | 'features'>()
  });

export type EditStaffOrganizationQuery = z.input<
  ReturnType<typeof editStaffOrganizationQuery>
>;

export function editStaffOrganizationPath(
  pathParams: { id: Param },
  queryParams: EditStaffOrganizationQuery = {}
): string {
  return format(
    '/staff/organizations/:id/edit',
    pathParams,
    globalWire.parse(queryParams ?? {}),
    { translate: true, prependLocale: true }
  );
}

const editStaffOrganizationContractSegments = lazy(() =>
  z
    .object({ organizationId: segment, id: segment })
    .transform(({ organizationId, id }) => ({
      organization_id: organizationId,
      id
    }))
);

export type EditStaffOrganizationContractSegments = z.input<
  ReturnType<typeof editStaffOrganizationContractSegments>
>;

export function editStaffOrganizationContractPath(
  pathParams: EditStaffOrganizationContractSegments,
  queryParams: GlobalQuery = {}
): string {
  return format(
    '/staff/organizations/:organization_id/contracts/:id/edit',
    editStaffOrganizationContractSegments().parse(pathParams),
    globalWire.parse(queryParams ?? {}),
    { translate: true, prependLocale: true }
  );
}

const editStaffOrganizationLinkQuery = () =>
  z.object({
    ...globalQuery,
    showAllSites: queryValue
  });

export type EditStaffOrganizationLinkQuery = z.input<
  ReturnType<typeof editStaffOrganizationLinkQuery>
>;

const editStaffOrganizationLinkWire = lazy(() =>
  editStaffOrganizationLinkQuery()
    .catchall(queryValue)
    .transform(({ showAllSites, disableFeatures, ...query }) => ({
      show_all_sites: showAllSites,
      disable_features: disableFeatures,
      ...query
    }))
);

export function editStaffOrganizationLinkPath(
  pathParams: { id: Param },
  queryParams: EditStaffOrganizationLinkQuery = {}
): string {
  return format(
    '/staff/organization_links/:id/edit',
    pathParams,
    editStaffOrganizationLinkWire().parse(queryParams ?? {}),
    { translate: true, prependLocale: true }
  );
}

const editStaffOrganizationScheduledFeeSegments = lazy(() =>
  z
    .object({ organizationId: segment, id: segment })
    .transform(({ organizationId, id }) => ({
      organization_id: organizationId,
      id
    }))
);

export type EditStaffOrganizationScheduledFeeSegments = z.input<
  ReturnType<typeof editStaffOrganizationScheduledFeeSegments>
>;

export function editStaffOrganizationScheduledFeePath(
  pathParams: EditStaffOrganizationScheduledFeeSegments,
  queryParams: GlobalQuery = {}
): string {
  return format(
    '/staff/organizations/:organization_id/scheduled_fees/:id/edit',
    editStaffOrganizationScheduledFeeSegments().parse(pathParams),
    globalWire.parse(queryParams ?? {}),
    { translate: true, prependLocale: true }
  );
}

const editStaffOrganizationServiceFeeSegments = lazy(() =>
  z
    .object({ organizationId: segment, id: segment })
    .transform(({ organizationId, id }) => ({
      organization_id: organizationId,
      id
    }))
);

export type EditStaffOrganizationServiceFeeSegments = z.input<
  ReturnType<typeof editStaffOrganizationServiceFeeSegments>
>;

export function editStaffOrganizationServiceFeePath(
  pathParams: EditStaffOrganizationServiceFeeSegments,
  queryParams: GlobalQuery = {}
): string {
  return format(
    '/staff/organizations/:organization_id/service_fees/:id/edit',
    editStaffOrganizationServiceFeeSegments().parse(pathParams),
    globalWire.parse(queryParams ?? {}),
    { translate: true, prependLocale: true }
  );
}

export function editStaffSearchSupplierSearchExcludedProfilePath(
  pathParams: { id: Param },
  queryParams: GlobalQuery = {}
): string {
  return format(
    '/staff/supplier_search_excluded_profiles/:id/edit',
    pathParams,
    globalWire.parse(queryParams ?? {}),
    { translate: true, prependLocale: true }
  );
}

export function editStaffSearchSupplierSearchTopLevelCategoryPath(
  pathParams: { id: Param },
  queryParams: GlobalQuery = {}
): string {
  return format(
    '/staff/supplier_search_top_level_categories/:id/edit',
    pathParams,
    globalWire.parse(queryParams ?? {}),
    { translate: true, prependLocale: true }
  );
}

const editStaffSearchSupplierSearchTopLevelCategorySubCategorySegments = lazy(
  () =>
    z
      .object({ supplierSearchTopLevelCategoryId: segment, id: segment })
      .transform(({ supplierSearchTopLevelCategoryId, id }) => ({
        supplier_search_top_level_category_id: supplierSearchTopLevelCategoryId,
        id
      }))
);

export type EditStaffSearchSupplierSearchTopLevelCategorySubCategorySegments =
  z.input<
    ReturnType<
      typeof editStaffSearchSupplierSearchTopLevelCategorySubCategorySegments
    >
  >;

export function editStaffSearchSupplierSearchTopLevelCategorySubCategoryPath(
  pathParams: EditStaffSearchSupplierSearchTopLevelCategorySubCategorySegments,
  queryParams: GlobalQuery = {}
): string {
  return format(
    '/staff/supplier_search_top_level_categories/:supplier_search_top_level_category_id/sub_categories/:id/edit',
    editStaffSearchSupplierSearchTopLevelCategorySubCategorySegments().parse(
      pathParams
    ),
    globalWire.parse(queryParams ?? {}),
    { translate: true, prependLocale: true }
  );
}

export function editStaffSiteManagementLocationGeoShapePath(
  pathParams: { id: Param },
  queryParams: GlobalQuery = {}
): string {
  return format(
    '/staff/site_management/location_geo_shapes/:id/edit',
    pathParams,
    globalWire.parse(queryParams ?? {}),
    { translate: true, prependLocale: true }
  );
}

export function editStaffUserPath(
  pathParams: { id: Param },
  queryParams: GlobalQuery = {}
): string {
  return format(
    '/staff/users/:id/edit',
    pathParams,
    globalWire.parse(queryParams ?? {}),
    { translate: true, prependLocale: true }
  );
}

export function editStaffVenuePath(
  pathParams: { id: Param },
  queryParams: GlobalQuery = {}
): string {
  return format(
    '/staff/venues/:id/edit',
    pathParams,
    globalWire.parse(queryParams ?? {}),
    { translate: true, prependLocale: true }
  );
}

const editStaffVenueRelevanceScoresSegments = lazy(() =>
  z
    .object({ venueId: segment })
    .transform(({ venueId }) => ({ venue_id: venueId }))
);

export type EditStaffVenueRelevanceScoresSegments = z.input<
  ReturnType<typeof editStaffVenueRelevanceScoresSegments>
>;

export function editStaffVenueRelevanceScoresPath(
  pathParams: EditStaffVenueRelevanceScoresSegments,
  queryParams: GlobalQuery = {}
): string {
  return format(
    '/staff/venues/:venue_id/relevance_scores/edit',
    editStaffVenueRelevanceScoresSegments().parse(pathParams),
    globalWire.parse(queryParams ?? {}),
    { translate: true, prependLocale: true }
  );
}

const editUserPasswordQuery = () =>
  z.object({
    ...globalQuery,
    resetPasswordToken: queryValue
  });

export type EditUserPasswordQuery = z.input<
  ReturnType<typeof editUserPasswordQuery>
>;

const editUserPasswordWire = lazy(() =>
  editUserPasswordQuery()
    .catchall(queryValue)
    .transform(({ resetPasswordToken, disableFeatures, ...query }) => ({
      reset_password_token: resetPasswordToken,
      disable_features: disableFeatures,
      ...query
    }))
);

export function editUserPasswordPath(
  queryParams: EditUserPasswordQuery = {}
): string {
  return format(
    '/password/edit',
    {},
    editUserPasswordWire().parse(queryParams ?? {}),
    { translate: true, prependLocale: true }
  );
}

export function editUserRegistrationPath(
  queryParams: GlobalQuery = {}
): string {
  return format('/edit', {}, globalWire.parse(queryParams ?? {}), {
    translate: true,
    prependLocale: true
  });
}

export function enableFeatureStaffOrganizationPath(
  pathParams: { id: Param },
  queryParams: GlobalQuery = {}
): string {
  return format(
    '/staff/organizations/:id/enable_feature',
    pathParams,
    globalWire.parse(queryParams ?? {}),
    { translate: true, prependLocale: true }
  );
}

export function examplePath(queryParams: GlobalQuery = {}): string {
  return format('/example', {}, globalWire.parse(queryParams ?? {}), {
    translate: true,
    prependLocale: true
  });
}

export function exportAnnualInvoicedSalesBreakdownByOrgStaffCSVExportsPath(
  queryParams: GlobalQuery = {}
): string {
  return format(
    '/staff/csv_exports/export_annual_invoiced_sales_breakdown_by_org',
    {},
    globalWire.parse(queryParams ?? {}),
    { translate: true, prependLocale: true }
  );
}

export function exportAnnualInvoicedSalesByOrgStaffCSVExportsPath(
  queryParams: GlobalQuery = {}
): string {
  return format(
    '/staff/csv_exports/export_annual_invoiced_sales_by_org',
    {},
    globalWire.parse(queryParams ?? {}),
    { translate: true, prependLocale: true }
  );
}

export function exportAskForReviewsStaffCSVExportsPath(
  queryParams: GlobalQuery = {}
): string {
  return format(
    '/staff/csv_exports/export_ask_for_reviews',
    {},
    globalWire.parse(queryParams ?? {}),
    { translate: true, prependLocale: true }
  );
}

export function exportCateringProfilesStaffCSVExportsPath(
  queryParams: GlobalQuery = {}
): string {
  return format(
    '/staff/csv_exports/export_catering_profiles',
    {},
    globalWire.parse(queryParams ?? {}),
    { translate: true, prependLocale: true }
  );
}

export function exportEventRequestsStaffCSVExportsPath(
  queryParams: GlobalQuery = {}
): string {
  return format(
    '/staff/csv_exports/export_event_requests',
    {},
    globalWire.parse(queryParams ?? {}),
    { translate: true, prependLocale: true }
  );
}

export function exportInquiriesByOrgStaffCSVExportsPath(
  queryParams: GlobalQuery = {}
): string {
  return format(
    '/staff/csv_exports/export_inquiries_by_org',
    {},
    globalWire.parse(queryParams ?? {}),
    { translate: true, prependLocale: true }
  );
}

export function exportLandingPagesStaffCSVExportsPath(
  queryParams: GlobalQuery = {}
): string {
  return format(
    '/staff/csv_exports/export_landing_pages',
    {},
    globalWire.parse(queryParams ?? {}),
    { translate: true, prependLocale: true }
  );
}

export function exportNonResponsiveAssigneesStaffCSVExportsPath(
  queryParams: GlobalQuery = {}
): string {
  return format(
    '/staff/csv_exports/export_non_responsive_assignees',
    {},
    globalWire.parse(queryParams ?? {}),
    { translate: true, prependLocale: true }
  );
}

export function exportNotRespondedInquiriesStaffCSVExportsPath(
  queryParams: GlobalQuery = {}
): string {
  return format(
    '/staff/csv_exports/export_not_responded_inquiries',
    {},
    globalWire.parse(queryParams ?? {}),
    { translate: true, prependLocale: true }
  );
}

export function exportOrganizationsStaffCSVExportsPath(
  queryParams: GlobalQuery = {}
): string {
  return format(
    '/staff/csv_exports/export_organizations',
    {},
    globalWire.parse(queryParams ?? {}),
    { translate: true, prependLocale: true }
  );
}

export function exportOrganizationsWithInternalCommentStaffCSVExportsPath(
  queryParams: GlobalQuery = {}
): string {
  return format(
    '/staff/csv_exports/export_organizations_with_internal_comment',
    {},
    globalWire.parse(queryParams ?? {}),
    { translate: true, prependLocale: true }
  );
}

export function exportOrganizationsWithPackagesStaffCSVExportsPath(
  queryParams: GlobalQuery = {}
): string {
  return format(
    '/staff/csv_exports/export_organizations_with_packages',
    {},
    globalWire.parse(queryParams ?? {}),
    { translate: true, prependLocale: true }
  );
}

export function exportSupplierOrganizationsStaffCSVExportsPath(
  queryParams: GlobalQuery = {}
): string {
  return format(
    '/staff/csv_exports/export_supplier_organizations',
    {},
    globalWire.parse(queryParams ?? {}),
    { translate: true, prependLocale: true }
  );
}

export function exportSupplierProfilesStaffCSVExportsPath(
  queryParams: GlobalQuery = {}
): string {
  return format(
    '/staff/csv_exports/export_supplier_profiles',
    {},
    globalWire.parse(queryParams ?? {}),
    { translate: true, prependLocale: true }
  );
}

export function exportUsersStaffCSVExportsPath(
  queryParams: GlobalQuery = {}
): string {
  return format(
    '/staff/csv_exports/export_users',
    {},
    globalWire.parse(queryParams ?? {}),
    { translate: true, prependLocale: true }
  );
}

export function exportVenuesRelevanceScoresStaffCSVExportsPath(
  queryParams: GlobalQuery = {}
): string {
  return format(
    '/staff/csv_exports/export_venues_relevance_scores',
    {},
    globalWire.parse(queryParams ?? {}),
    { translate: true, prependLocale: true }
  );
}

export function exportVenuesStaffCSVExportsPath(
  queryParams: GlobalQuery = {}
): string {
  return format(
    '/staff/csv_exports/export_venues',
    {},
    globalWire.parse(queryParams ?? {}),
    { translate: true, prependLocale: true }
  );
}

export function imprintPath(queryParams: GlobalQuery = {}): string {
  return format('/imprint', {}, globalWire.parse(queryParams ?? {}), {
    translate: true,
    prependLocale: true
  });
}

export function indexPath(queryParams: GlobalQuery = {}): string {
  return format('/index', {}, globalWire.parse(queryParams ?? {}), {
    translate: true,
    prependLocale: true
  });
}

const inquiriesSupplierProfileSupplierInquiriesSegments = lazy(() =>
  z
    .object({ supplierProfileId: segment })
    .transform(({ supplierProfileId }) => ({
      supplier_profile_id: supplierProfileId
    }))
);

export type InquiriesSupplierProfileSupplierInquiriesSegments = z.input<
  ReturnType<typeof inquiriesSupplierProfileSupplierInquiriesSegments>
>;

export function inquiriesSupplierProfileSupplierInquiriesPath(
  pathParams: InquiriesSupplierProfileSupplierInquiriesSegments,
  queryParams: GlobalQuery = {}
): string {
  return format(
    '/sp/:supplier_profile_id/inquiries',
    inquiriesSupplierProfileSupplierInquiriesSegments().parse(pathParams),
    globalWire.parse(queryParams ?? {}),
    { translate: true, prependLocale: true }
  );
}

export function instructionsICalPath(queryParams: GlobalQuery = {}): string {
  return format('/instructions/ical', {}, globalWire.parse(queryParams ?? {}), {
    translate: true,
    prependLocale: true
  });
}

export function instructionsReportBookingsPath(
  queryParams: GlobalQuery = {}
): string {
  return format(
    '/instructions/report-bookings',
    {},
    globalWire.parse(queryParams ?? {}),
    { translate: true, prependLocale: true }
  );
}

export function kisakatsomoskabanVoittajaPath(
  queryParams: GlobalQuery = {}
): string {
  return format(
    '/kisakatsomoskaban-voittaja',
    {},
    globalWire.parse(queryParams ?? {}),
    { translate: true, prependLocale: true }
  );
}

export function landingPagePath(
  pathParams: { id: Param },
  queryParams: GlobalQuery = {}
): string {
  return format('/:id', pathParams, globalWire.parse(queryParams ?? {}), {
    translate: true,
    prependLocale: true
  });
}

export function listPath(
  pathParams: { id: Param },
  queryParams: GlobalQuery = {}
): string {
  return format('/lists/:id', pathParams, globalWire.parse(queryParams ?? {}), {
    translate: true,
    prependLocale: true
  });
}

export function listsPath(queryParams: GlobalQuery = {}): string {
  return format('/lists', {}, globalWire.parse(queryParams ?? {}), {
    translate: true,
    prependLocale: true
  });
}

export function mailEventsStaffCommonMailEventsMailEventPath(
  pathParams: { id: Param },
  queryParams: GlobalQuery = {}
): string {
  return format(
    '/staff/mail_events/:id',
    pathParams,
    globalWire.parse(queryParams ?? {}),
    { translate: true, prependLocale: true }
  );
}

const mailEventsStaffCommonMailEventsMailEventsQuery = () =>
  z.object({
    ...globalQuery,
    page: queryValue
  });

export type MailEventsStaffCommonMailEventsMailEventsQuery = z.input<
  ReturnType<typeof mailEventsStaffCommonMailEventsMailEventsQuery>
>;

export function mailEventsStaffCommonMailEventsMailEventsPath(
  queryParams: MailEventsStaffCommonMailEventsMailEventsQuery = {}
): string {
  return format('/staff/mail_events', {}, globalWire.parse(queryParams ?? {}), {
    translate: true,
    prependLocale: true
  });
}

export function mailEventsWebhooksCommonMailEventsWebhooksPath(
  queryParams: GlobalQuery = {}
): string {
  return format(
    '/webhooks/mail_events',
    {},
    globalWire.parse(queryParams ?? {}),
    { translate: true, prependLocale: true }
  );
}

export function manageVenuesPath(queryParams: GlobalQuery = {}): string {
  return format('/dashboard/venues', {}, globalWire.parse(queryParams ?? {}), {
    translate: true,
    prependLocale: true
  });
}

export function markAsNotSpamSpamInquiriesStaffCommonSpamInquiriesPath(
  queryParams: GlobalQuery = {}
): string {
  return format(
    '/staff/spam_inquiries/mark_as_not_spam',
    {},
    globalWire.parse(queryParams ?? {}),
    { translate: true, prependLocale: true }
  );
}

export function markAsRepliedAdminPriceRequestPath(
  pathParams: { id: Param },
  queryParams: GlobalQuery = {}
): string {
  return format(
    '/admin/inquiries/:id/mark_as_replied',
    pathParams,
    globalWire.parse(queryParams ?? {}),
    { translate: true, prependLocale: true }
  );
}

export function markAsSpamSpamInquiriesStaffCommonSpamInquiriesPath(
  queryParams: GlobalQuery = {}
): string {
  return format(
    '/staff/spam_inquiries/mark_as_spam',
    {},
    globalWire.parse(queryParams ?? {}),
    { translate: true, prependLocale: true }
  );
}

export function newListPath(queryParams: GlobalQuery = {}): string {
  return format('/lists/new', {}, globalWire.parse(queryParams ?? {}), {
    translate: true,
    prependLocale: true
  });
}

const newPriceRequestQuery = () =>
  z.object({
    ...globalQuery,
    venueIds: queryValue
  });

export type NewPriceRequestQuery = z.input<
  ReturnType<typeof newPriceRequestQuery>
>;

const newPriceRequestWire = lazy(() =>
  newPriceRequestQuery()
    .catchall(queryValue)
    .transform(({ venueIds, disableFeatures, ...query }) => ({
      venue_ids: venueIds,
      disable_features: disableFeatures,
      ...query
    }))
);

export function newPriceRequestPath(
  queryParams: NewPriceRequestQuery = {}
): string {
  return format(
    '/price_requests/new',
    {},
    newPriceRequestWire().parse(queryParams ?? {}),
    { translate: true, prependLocale: true }
  );
}

const newProfilesCateringProfileReviewSegments = lazy(() =>
  z
    .object({ cateringProfileId: segment })
    .transform(({ cateringProfileId }) => ({
      catering_profile_id: cateringProfileId
    }))
);

export type NewProfilesCateringProfileReviewSegments = z.input<
  ReturnType<typeof newProfilesCateringProfileReviewSegments>
>;

export function newProfilesCateringProfileReviewPath(
  pathParams: NewProfilesCateringProfileReviewSegments,
  queryParams: GlobalQuery = {}
): string {
  return format(
    '/catering/:catering_profile_id/reviews/new',
    newProfilesCateringProfileReviewSegments().parse(pathParams),
    globalWire.parse(queryParams ?? {}),
    { translate: true, prependLocale: true }
  );
}

const newProfilesVenueProfileReviewSegments = lazy(() =>
  z
    .object({ venueProfileId: segment })
    .transform(({ venueProfileId }) => ({ venue_profile_id: venueProfileId }))
);

export type NewProfilesVenueProfileReviewSegments = z.input<
  ReturnType<typeof newProfilesVenueProfileReviewSegments>
>;

export function newProfilesVenueProfileReviewPath(
  pathParams: NewProfilesVenueProfileReviewSegments,
  queryParams: GlobalQuery = {}
): string {
  return format(
    '/venues/:venue_profile_id/reviews/new',
    newProfilesVenueProfileReviewSegments().parse(pathParams),
    globalWire.parse(queryParams ?? {}),
    { translate: true, prependLocale: true }
  );
}

export function newRailsConductorInboundEmailPath(
  queryParams: GlobalQuery = {}
): string {
  return format(
    '/rails/conductor/action_mailbox/inbound_emails/new',
    {},
    globalWire.parse(queryParams ?? {}),
    { translate: true, prependLocale: true }
  );
}

export function newRailsConductorInboundEmailSourcePath(
  queryParams: GlobalQuery = {}
): string {
  return format(
    '/rails/conductor/action_mailbox/inbound_emails/sources/new',
    {},
    globalWire.parse(queryParams ?? {}),
    { translate: true, prependLocale: true }
  );
}

export function newStaffCateringLandingPagePath(
  queryParams: GlobalQuery = {}
): string {
  return format(
    '/staff/catering_landing_pages/new',
    {},
    globalWire.parse(queryParams ?? {}),
    { translate: true, prependLocale: true }
  );
}

export function newStaffInvoicePath(queryParams: GlobalQuery = {}): string {
  return format(
    '/staff/invoices/new',
    {},
    globalWire.parse(queryParams ?? {}),
    { translate: true, prependLocale: true }
  );
}

export function newStaffLandingPagePath(queryParams: GlobalQuery = {}): string {
  return format(
    '/staff/landing_pages/new',
    {},
    globalWire.parse(queryParams ?? {}),
    { translate: true, prependLocale: true }
  );
}

export function newStaffOrganizationPath(
  queryParams: GlobalQuery = {}
): string {
  return format(
    '/staff/organizations/new',
    {},
    globalWire.parse(queryParams ?? {}),
    { translate: true, prependLocale: true }
  );
}

const newStaffOrganizationContractQuery = () =>
  z.object({
    ...globalQuery,
    contractType: queryValue
  });

export type NewStaffOrganizationContractQuery = z.input<
  ReturnType<typeof newStaffOrganizationContractQuery>
>;

const newStaffOrganizationContractWire = lazy(() =>
  newStaffOrganizationContractQuery()
    .catchall(queryValue)
    .transform(({ contractType, disableFeatures, ...query }) => ({
      contract_type: contractType,
      disable_features: disableFeatures,
      ...query
    }))
);

const newStaffOrganizationContractSegments = lazy(() =>
  z
    .object({ organizationId: segment })
    .transform(({ organizationId }) => ({ organization_id: organizationId }))
);

export type NewStaffOrganizationContractSegments = z.input<
  ReturnType<typeof newStaffOrganizationContractSegments>
>;

export function newStaffOrganizationContractPath(
  pathParams: NewStaffOrganizationContractSegments,
  queryParams: NewStaffOrganizationContractQuery = {}
): string {
  return format(
    '/staff/organizations/:organization_id/contracts/new',
    newStaffOrganizationContractSegments().parse(pathParams),
    newStaffOrganizationContractWire().parse(queryParams ?? {}),
    { translate: true, prependLocale: true }
  );
}

const newStaffOrganizationLinkQuery = () =>
  z.object({
    ...globalQuery,
    showAllSites: queryValue
  });

export type NewStaffOrganizationLinkQuery = z.input<
  ReturnType<typeof newStaffOrganizationLinkQuery>
>;

const newStaffOrganizationLinkWire = lazy(() =>
  newStaffOrganizationLinkQuery()
    .catchall(queryValue)
    .transform(({ showAllSites, disableFeatures, ...query }) => ({
      show_all_sites: showAllSites,
      disable_features: disableFeatures,
      ...query
    }))
);

export function newStaffOrganizationLinkPath(
  queryParams: NewStaffOrganizationLinkQuery = {}
): string {
  return format(
    '/staff/organization_links/new',
    {},
    newStaffOrganizationLinkWire().parse(queryParams ?? {}),
    { translate: true, prependLocale: true }
  );
}

const newStaffOrganizationScheduledFeeSegments = lazy(() =>
  z
    .object({ organizationId: segment })
    .transform(({ organizationId }) => ({ organization_id: organizationId }))
);

export type NewStaffOrganizationScheduledFeeSegments = z.input<
  ReturnType<typeof newStaffOrganizationScheduledFeeSegments>
>;

export function newStaffOrganizationScheduledFeePath(
  pathParams: NewStaffOrganizationScheduledFeeSegments,
  queryParams: GlobalQuery = {}
): string {
  return format(
    '/staff/organizations/:organization_id/scheduled_fees/new',
    newStaffOrganizationScheduledFeeSegments().parse(pathParams),
    globalWire.parse(queryParams ?? {}),
    { translate: true, prependLocale: true }
  );
}

const newStaffOrganizationServiceFeeSegments = lazy(() =>
  z
    .object({ organizationId: segment })
    .transform(({ organizationId }) => ({ organization_id: organizationId }))
);

export type NewStaffOrganizationServiceFeeSegments = z.input<
  ReturnType<typeof newStaffOrganizationServiceFeeSegments>
>;

export function newStaffOrganizationServiceFeePath(
  pathParams: NewStaffOrganizationServiceFeeSegments,
  queryParams: GlobalQuery = {}
): string {
  return format(
    '/staff/organizations/:organization_id/service_fees/new',
    newStaffOrganizationServiceFeeSegments().parse(pathParams),
    globalWire.parse(queryParams ?? {}),
    { translate: true, prependLocale: true }
  );
}

export function newStaffSearchSupplierSearchTopLevelCategoryPath(
  queryParams: GlobalQuery = {}
): string {
  return format(
    '/staff/supplier_search_top_level_categories/new',
    {},
    globalWire.parse(queryParams ?? {}),
    { translate: true, prependLocale: true }
  );
}

const newStaffSearchSupplierSearchTopLevelCategorySubCategorySegments = lazy(
  () =>
    z
      .object({ supplierSearchTopLevelCategoryId: segment })
      .transform(({ supplierSearchTopLevelCategoryId }) => ({
        supplier_search_top_level_category_id: supplierSearchTopLevelCategoryId
      }))
);

export type NewStaffSearchSupplierSearchTopLevelCategorySubCategorySegments =
  z.input<
    ReturnType<
      typeof newStaffSearchSupplierSearchTopLevelCategorySubCategorySegments
    >
  >;

export function newStaffSearchSupplierSearchTopLevelCategorySubCategoryPath(
  pathParams: NewStaffSearchSupplierSearchTopLevelCategorySubCategorySegments,
  queryParams: GlobalQuery = {}
): string {
  return format(
    '/staff/supplier_search_top_level_categories/:supplier_search_top_level_category_id/sub_categories/new',
    newStaffSearchSupplierSearchTopLevelCategorySubCategorySegments().parse(
      pathParams
    ),
    globalWire.parse(queryParams ?? {}),
    { translate: true, prependLocale: true }
  );
}

export function newStaffSiteManagementLocationGeoShapePath(
  queryParams: GlobalQuery = {}
): string {
  return format(
    '/staff/site_management/location_geo_shapes/new',
    {},
    globalWire.parse(queryParams ?? {}),
    { translate: true, prependLocale: true }
  );
}

export function newStaffVenuePath(queryParams: GlobalQuery = {}): string {
  return format('/staff/venues/new', {}, globalWire.parse(queryParams ?? {}), {
    translate: true,
    prependLocale: true
  });
}

export function newUserConfirmationPath(queryParams: GlobalQuery = {}): string {
  return format('/confirmation/new', {}, globalWire.parse(queryParams ?? {}), {
    translate: true,
    prependLocale: true
  });
}

const newUserInvitationQuery = () =>
  z.object({
    ...globalQuery,
    organizationId: queryValue
  });

export type NewUserInvitationQuery = z.input<
  ReturnType<typeof newUserInvitationQuery>
>;

const newUserInvitationWire = lazy(() =>
  newUserInvitationQuery()
    .catchall(queryValue)
    .transform(({ organizationId, disableFeatures, ...query }) => ({
      organization_id: organizationId,
      disable_features: disableFeatures,
      ...query
    }))
);

export function newUserInvitationPath(
  queryParams: NewUserInvitationQuery = {}
): string {
  return format(
    '/invitation/new',
    {},
    newUserInvitationWire().parse(queryParams ?? {}),
    { translate: true, prependLocale: true }
  );
}

export function newUserPasswordPath(queryParams: GlobalQuery = {}): string {
  return format('/password/new', {}, globalWire.parse(queryParams ?? {}), {
    translate: true,
    prependLocale: true
  });
}

const newUserRegistrationQuery = () =>
  z.object({
    ...globalQuery,
    referralCode: queryValue
  });

export type NewUserRegistrationQuery = z.input<
  ReturnType<typeof newUserRegistrationQuery>
>;

const newUserRegistrationWire = lazy(() =>
  newUserRegistrationQuery()
    .catchall(queryValue)
    .transform(({ referralCode, disableFeatures, ...query }) => ({
      referral_code: referralCode,
      disable_features: disableFeatures,
      ...query
    }))
);

export function newUserRegistrationPath(
  queryParams: NewUserRegistrationQuery = {}
): string {
  return format(
    '/register',
    {},
    newUserRegistrationWire().parse(queryParams ?? {}),
    { translate: true, prependLocale: true }
  );
}

export function newUserSessionPath(queryParams: GlobalQuery = {}): string {
  return format('/login', {}, globalWire.parse(queryParams ?? {}), {
    translate: true,
    prependLocale: true
  });
}

export function newVenueOwnerFormPath(queryParams: GlobalQuery = {}): string {
  return format(
    '/register-venue-owner',
    {},
    globalWire.parse(queryParams ?? {}),
    { translate: true, prependLocale: true }
  );
}

export function newVenueOwnerRegistrationPath(
  queryParams: GlobalQuery = {}
): string {
  return format('/add-your-venue', {}, globalWire.parse(queryParams ?? {}), {
    translate: true,
    prependLocale: true
  });
}

const newVenueReviewSegments = lazy(() =>
  z
    .object({ venueId: segment })
    .transform(({ venueId }) => ({ venue_id: venueId }))
);

export type NewVenueReviewSegments = z.input<
  ReturnType<typeof newVenueReviewSegments>
>;

export function newVenueReviewPath(
  pathParams: NewVenueReviewSegments,
  queryParams: GlobalQuery = {}
): string {
  return format(
    '/venues/:venue_id/reviews/new',
    newVenueReviewSegments().parse(pathParams),
    globalWire.parse(queryParams ?? {}),
    { translate: true, prependLocale: true }
  );
}

export function noneOutcomesCommonOutcomesEventCandidatePath(
  pathParams: { token: Param },
  queryParams: GlobalQuery = {}
): string {
  return format(
    '/event_feedback/:token/none',
    pathParams,
    globalWire.parse(queryParams ?? {}),
    { translate: true, prependLocale: true }
  );
}

export function offPlatformOutcomesCommonOutcomesEventCandidatePath(
  pathParams: { token: Param },
  queryParams: GlobalQuery = {}
): string {
  return format(
    '/event_feedback/:token/off_platform',
    pathParams,
    globalWire.parse(queryParams ?? {}),
    { translate: true, prependLocale: true }
  );
}

const orderAdminPackagesQuery = () =>
  z.object({
    ...globalQuery,
    organizationId: queryValue,
    packageType: oneOf<
      | 'celebration_packages'
      | 'experience_packages'
      | 'meeting_packages'
      | 'venue_packages'
    >(),
    shownVenueId: queryValue,
    venueId: queryValue
  });

export type OrderAdminPackagesQuery = z.input<
  ReturnType<typeof orderAdminPackagesQuery>
>;

const orderAdminPackagesWire = lazy(() =>
  orderAdminPackagesQuery()
    .catchall(queryValue)
    .transform(
      ({
        organizationId,
        packageType,
        shownVenueId,
        venueId,
        disableFeatures,
        ...query
      }) => ({
        organization_id: organizationId,
        package_type: packageType,
        shown_venue_id: shownVenueId,
        venue_id: venueId,
        disable_features: disableFeatures,
        ...query
      })
    )
);

export function orderAdminPackagesPath(
  queryParams: OrderAdminPackagesQuery = {}
): string {
  return format(
    '/admin/packages/order',
    {},
    orderAdminPackagesWire().parse(queryParams ?? {}),
    { translate: true, prependLocale: true }
  );
}

export function outcomesAPICommonOutcomesEventCandidatePath(
  pathParams: { token: Param },
  queryParams: GlobalQuery = {}
): string {
  return format(
    '/api/event_candidates/:token',
    pathParams,
    globalWire.parse(queryParams ?? {}),
    { translate: false, prependLocale: false }
  );
}

const outcomesAPIPossibleOutcomePipedriveProjectSegments = lazy(() =>
  z
    .object({ possibleOutcomeId: segment })
    .transform(({ possibleOutcomeId }) => ({
      possible_outcome_id: possibleOutcomeId
    }))
);

export type OutcomesAPIPossibleOutcomePipedriveProjectSegments = z.input<
  ReturnType<typeof outcomesAPIPossibleOutcomePipedriveProjectSegments>
>;

export function outcomesAPIPossibleOutcomePipedriveProjectPath(
  pathParams: OutcomesAPIPossibleOutcomePipedriveProjectSegments,
  queryParams: GlobalQuery = {}
): string {
  return format(
    '/api/possible_outcomes/:possible_outcome_id/pipedrive_project',
    outcomesAPIPossibleOutcomePipedriveProjectSegments().parse(pathParams),
    globalWire.parse(queryParams ?? {}),
    { translate: false, prependLocale: false }
  );
}

export function partnerInboundListYourEventServicePath(
  queryParams: GlobalQuery = {}
): string {
  return format(
    '/s/list-your-event-service',
    {},
    globalWire.parse(queryParams ?? {}),
    { translate: true, prependLocale: true }
  );
}

export function personaDestroyAdminInquiryRecipientsPath(
  pathParams: { id: Param },
  queryParams: GlobalQuery = {}
): string {
  return format(
    '/admin/inquiry_recipients/:id',
    pathParams,
    globalWire.parse(queryParams ?? {}),
    { translate: true, prependLocale: true }
  );
}

export function personaToggleDisabledAdminInquiryRecipientsPath(
  pathParams: { id: Param },
  queryParams: GlobalQuery = {}
): string {
  return format(
    '/admin/inquiry_recipients/:id/toggle_disabled',
    pathParams,
    globalWire.parse(queryParams ?? {}),
    { translate: true, prependLocale: true }
  );
}

export function personaUpdateAdminInquiryRecipientsPath(
  pathParams: { id: Param },
  queryParams: GlobalQuery = {}
): string {
  return format(
    '/admin/inquiry_recipients/:id',
    pathParams,
    globalWire.parse(queryParams ?? {}),
    { translate: true, prependLocale: true }
  );
}

export function pitopalveluPath(queryParams: GlobalQuery = {}): string {
  return format('/pitopalvelu', {}, globalWire.parse(queryParams ?? {}), {
    translate: true,
    prependLocale: true
  });
}

export function pitopalveluEspooPath(queryParams: GlobalQuery = {}): string {
  return format('/pitopalvelu-espoo', {}, globalWire.parse(queryParams ?? {}), {
    translate: true,
    prependLocale: true
  });
}

export function pitopalveluHelsinkiPath(queryParams: GlobalQuery = {}): string {
  return format(
    '/pitopalvelu-helsinki',
    {},
    globalWire.parse(queryParams ?? {}),
    { translate: true, prependLocale: true }
  );
}

export function pitopalveluTamperePath(queryParams: GlobalQuery = {}): string {
  return format(
    '/pitopalvelu-tampere',
    {},
    globalWire.parse(queryParams ?? {}),
    { translate: true, prependLocale: true }
  );
}

export function pitopalveluTurkuPath(queryParams: GlobalQuery = {}): string {
  return format('/pitopalvelu-turku', {}, globalWire.parse(queryParams ?? {}), {
    translate: true,
    prependLocale: true
  });
}

export function pitopalveluVantaaPath(queryParams: GlobalQuery = {}): string {
  return format(
    '/pitopalvelu-vantaa',
    {},
    globalWire.parse(queryParams ?? {}),
    { translate: true, prependLocale: true }
  );
}

const priceRequestsQuery = () =>
  z.object({
    ...globalQuery,
    venueIds: queryValue
  });

export type PriceRequestsQuery = z.input<ReturnType<typeof priceRequestsQuery>>;

const priceRequestsWire = lazy(() =>
  priceRequestsQuery()
    .catchall(queryValue)
    .transform(({ venueIds, disableFeatures, ...query }) => ({
      venue_ids: venueIds,
      disable_features: disableFeatures,
      ...query
    }))
);

export function priceRequestsPath(
  queryParams: PriceRequestsQuery = {}
): string {
  return format(
    '/price_requests',
    {},
    priceRequestsWire().parse(queryParams ?? {}),
    { translate: true, prependLocale: true }
  );
}

export function privacyPolicyPath(queryParams: GlobalQuery = {}): string {
  return format('/privacy-policy', {}, globalWire.parse(queryParams ?? {}), {
    translate: true,
    prependLocale: true
  });
}

export function profilesCateringProfilePath(
  pathParams: { id: Param },
  queryParams: GlobalQuery = {}
): string {
  return format(
    '/catering/:id',
    pathParams,
    globalWire.parse(queryParams ?? {}),
    { translate: true, prependLocale: true }
  );
}

const profilesCateringProfileImagesSegments = lazy(() =>
  z
    .object({ cateringProfileId: segment })
    .transform(({ cateringProfileId }) => ({
      catering_profile_id: cateringProfileId
    }))
);

export type ProfilesCateringProfileImagesSegments = z.input<
  ReturnType<typeof profilesCateringProfileImagesSegments>
>;

export function profilesCateringProfileImagesPath(
  pathParams: ProfilesCateringProfileImagesSegments,
  queryParams: GlobalQuery = {}
): string {
  return format(
    '/catering/:catering_profile_id/images',
    profilesCateringProfileImagesSegments().parse(pathParams),
    globalWire.parse(queryParams ?? {}),
    { translate: true, prependLocale: true }
  );
}

const profilesCateringProfileReviewsSegments = lazy(() =>
  z
    .object({ cateringProfileId: segment })
    .transform(({ cateringProfileId }) => ({
      catering_profile_id: cateringProfileId
    }))
);

export type ProfilesCateringProfileReviewsSegments = z.input<
  ReturnType<typeof profilesCateringProfileReviewsSegments>
>;

export function profilesCateringProfileReviewsPath(
  pathParams: ProfilesCateringProfileReviewsSegments,
  queryParams: GlobalQuery = {}
): string {
  return format(
    '/catering/:catering_profile_id/reviews',
    profilesCateringProfileReviewsSegments().parse(pathParams),
    globalWire.parse(queryParams ?? {}),
    { translate: true, prependLocale: true }
  );
}

export function profilesCateringProfilesPath(
  queryParams: GlobalQuery = {}
): string {
  return format('/catering', {}, globalWire.parse(queryParams ?? {}), {
    translate: true,
    prependLocale: true
  });
}

export function profilesReviewPath(
  pathParams: { id: Param },
  queryParams: GlobalQuery = {}
): string {
  return format(
    '/reviews/:id',
    pathParams,
    globalWire.parse(queryParams ?? {}),
    { translate: true, prependLocale: true }
  );
}

export function profilesStaffVenueProfileDuplicationsPath(
  queryParams: GlobalQuery = {}
): string {
  return format(
    '/staff/venue_profile_duplications',
    {},
    globalWire.parse(queryParams ?? {}),
    { translate: true, prependLocale: true }
  );
}

export function profilesSupplierProfilePath(
  pathParams: { id: Param },
  queryParams: GlobalQuery = {}
): string {
  return format('/sp/:id', pathParams, globalWire.parse(queryParams ?? {}), {
    translate: true,
    prependLocale: true
  });
}

const profilesSupplierProfileImagesSegments = lazy(() =>
  z
    .object({ supplierProfileId: segment })
    .transform(({ supplierProfileId }) => ({
      supplier_profile_id: supplierProfileId
    }))
);

export type ProfilesSupplierProfileImagesSegments = z.input<
  ReturnType<typeof profilesSupplierProfileImagesSegments>
>;

export function profilesSupplierProfileImagesPath(
  pathParams: ProfilesSupplierProfileImagesSegments,
  queryParams: GlobalQuery = {}
): string {
  return format(
    '/sp/:supplier_profile_id/images',
    profilesSupplierProfileImagesSegments().parse(pathParams),
    globalWire.parse(queryParams ?? {}),
    { translate: true, prependLocale: true }
  );
}

export function profilesSupplierProfilesPath(
  queryParams: GlobalQuery = {}
): string {
  return format('/sp', {}, globalWire.parse(queryParams ?? {}), {
    translate: true,
    prependLocale: true
  });
}

export function profilesVenueProfilePath(
  pathParams: { id: Param },
  queryParams: GlobalQuery = {}
): string {
  return format(
    '/venues/:id',
    pathParams,
    globalWire.parse(queryParams ?? {}),
    { translate: true, prependLocale: true }
  );
}

const profilesVenueProfilePublishSegments = lazy(() =>
  z
    .object({ venueProfileId: segment })
    .transform(({ venueProfileId }) => ({ venue_profile_id: venueProfileId }))
);

export type ProfilesVenueProfilePublishSegments = z.input<
  ReturnType<typeof profilesVenueProfilePublishSegments>
>;

export function profilesVenueProfilePublishPath(
  pathParams: ProfilesVenueProfilePublishSegments,
  queryParams: GlobalQuery = {}
): string {
  return format(
    '/venues/:venue_profile_id/publish',
    profilesVenueProfilePublishSegments().parse(pathParams),
    globalWire.parse(queryParams ?? {}),
    { translate: true, prependLocale: true }
  );
}

const profilesVenueProfileReviewsSegments = lazy(() =>
  z
    .object({ venueProfileId: segment })
    .transform(({ venueProfileId }) => ({ venue_profile_id: venueProfileId }))
);

export type ProfilesVenueProfileReviewsSegments = z.input<
  ReturnType<typeof profilesVenueProfileReviewsSegments>
>;

export function profilesVenueProfileReviewsPath(
  pathParams: ProfilesVenueProfileReviewsSegments,
  queryParams: GlobalQuery = {}
): string {
  return format(
    '/venues/:venue_profile_id/reviews',
    profilesVenueProfileReviewsSegments().parse(pathParams),
    globalWire.parse(queryParams ?? {}),
    { translate: true, prependLocale: true }
  );
}

const profilesVenueProfileTransferQuery = () =>
  z.object({
    ...globalQuery,
    allSites: queryValue
  });

export type ProfilesVenueProfileTransferQuery = z.input<
  ReturnType<typeof profilesVenueProfileTransferQuery>
>;

const profilesVenueProfileTransferWire = lazy(() =>
  profilesVenueProfileTransferQuery()
    .catchall(queryValue)
    .transform(({ allSites, disableFeatures, ...query }) => ({
      all_sites: allSites,
      disable_features: disableFeatures,
      ...query
    }))
);

const profilesVenueProfileTransferSegments = lazy(() =>
  z
    .object({ venueProfileId: segment })
    .transform(({ venueProfileId }) => ({ venue_profile_id: venueProfileId }))
);

export type ProfilesVenueProfileTransferSegments = z.input<
  ReturnType<typeof profilesVenueProfileTransferSegments>
>;

export function profilesVenueProfileTransferPath(
  pathParams: ProfilesVenueProfileTransferSegments,
  queryParams: ProfilesVenueProfileTransferQuery = {}
): string {
  return format(
    '/venues/:venue_profile_id/transfer',
    profilesVenueProfileTransferSegments().parse(pathParams),
    profilesVenueProfileTransferWire().parse(queryParams ?? {}),
    { translate: true, prependLocale: true }
  );
}

const profilesVenueProfileUnpublishSegments = lazy(() =>
  z
    .object({ venueProfileId: segment })
    .transform(({ venueProfileId }) => ({ venue_profile_id: venueProfileId }))
);

export type ProfilesVenueProfileUnpublishSegments = z.input<
  ReturnType<typeof profilesVenueProfileUnpublishSegments>
>;

export function profilesVenueProfileUnpublishPath(
  pathParams: ProfilesVenueProfileUnpublishSegments,
  queryParams: GlobalQuery = {}
): string {
  return format(
    '/venues/:venue_profile_id/unpublish',
    profilesVenueProfileUnpublishSegments().parse(pathParams),
    globalWire.parse(queryParams ?? {}),
    { translate: true, prependLocale: true }
  );
}

export function profilesVenueProfilesPath(
  queryParams: GlobalQuery = {}
): string {
  return format('/venues', {}, globalWire.parse(queryParams ?? {}), {
    translate: true,
    prependLocale: true
  });
}

export function publishViewingTimeAdminPriceRequestPath(
  pathParams: { id: Param },
  queryParams: GlobalQuery = {}
): string {
  return format(
    '/admin/inquiries/:id/publish_viewing_time',
    pathParams,
    globalWire.parse(queryParams ?? {}),
    { translate: true, prependLocale: true }
  );
}

export function railsConductorInboundEmailPath(
  pathParams: { id: Param },
  queryParams: GlobalQuery = {}
): string {
  return format(
    '/rails/conductor/action_mailbox/inbound_emails/:id',
    pathParams,
    globalWire.parse(queryParams ?? {}),
    { translate: true, prependLocale: true }
  );
}

const railsConductorInboundEmailIncinerateSegments = lazy(() =>
  z
    .object({ inboundEmailId: segment })
    .transform(({ inboundEmailId }) => ({ inbound_email_id: inboundEmailId }))
);

export type RailsConductorInboundEmailIncinerateSegments = z.input<
  ReturnType<typeof railsConductorInboundEmailIncinerateSegments>
>;

export function railsConductorInboundEmailIncineratePath(
  pathParams: RailsConductorInboundEmailIncinerateSegments,
  queryParams: GlobalQuery = {}
): string {
  return format(
    '/rails/conductor/action_mailbox/:inbound_email_id/incinerate',
    railsConductorInboundEmailIncinerateSegments().parse(pathParams),
    globalWire.parse(queryParams ?? {}),
    { translate: true, prependLocale: true }
  );
}

const railsConductorInboundEmailRerouteSegments = lazy(() =>
  z
    .object({ inboundEmailId: segment })
    .transform(({ inboundEmailId }) => ({ inbound_email_id: inboundEmailId }))
);

export type RailsConductorInboundEmailRerouteSegments = z.input<
  ReturnType<typeof railsConductorInboundEmailRerouteSegments>
>;

export function railsConductorInboundEmailReroutePath(
  pathParams: RailsConductorInboundEmailRerouteSegments,
  queryParams: GlobalQuery = {}
): string {
  return format(
    '/rails/conductor/action_mailbox/:inbound_email_id/reroute',
    railsConductorInboundEmailRerouteSegments().parse(pathParams),
    globalWire.parse(queryParams ?? {}),
    { translate: true, prependLocale: true }
  );
}

export function railsConductorInboundEmailSourcesPath(
  queryParams: GlobalQuery = {}
): string {
  return format(
    '/rails/conductor/action_mailbox/inbound_emails/sources',
    {},
    globalWire.parse(queryParams ?? {}),
    { translate: true, prependLocale: true }
  );
}

export function railsConductorInboundEmailsPath(
  queryParams: GlobalQuery = {}
): string {
  return format(
    '/rails/conductor/action_mailbox/inbound_emails',
    {},
    globalWire.parse(queryParams ?? {}),
    { translate: true, prependLocale: true }
  );
}

export function railsHealthCheckPath(queryParams: GlobalQuery = {}): string {
  return format('/up', {}, globalWire.parse(queryParams ?? {}), {
    translate: false,
    prependLocale: false
  });
}

export function railsInfoPath(queryParams: GlobalQuery = {}): string {
  return format('/rails/info', {}, globalWire.parse(queryParams ?? {}), {
    translate: true,
    prependLocale: true
  });
}

export function railsInfoNotesPath(queryParams: GlobalQuery = {}): string {
  return format('/rails/info/notes', {}, globalWire.parse(queryParams ?? {}), {
    translate: true,
    prependLocale: true
  });
}

export function railsInfoPropertiesPath(queryParams: GlobalQuery = {}): string {
  return format(
    '/rails/info/properties',
    {},
    globalWire.parse(queryParams ?? {}),
    { translate: true, prependLocale: true }
  );
}

export function railsInfoRoutesPath(queryParams: GlobalQuery = {}): string {
  return format('/rails/info/routes', {}, globalWire.parse(queryParams ?? {}), {
    translate: true,
    prependLocale: true
  });
}

export function railsMailersPath(queryParams: GlobalQuery = {}): string {
  return format('/rails/mailers', {}, globalWire.parse(queryParams ?? {}), {
    translate: true,
    prependLocale: true
  });
}

export function railsMailgunInboundEmailsPath(
  queryParams: GlobalQuery = {}
): string {
  return format(
    '/rails/action_mailbox/mailgun/inbound_emails/mime',
    {},
    globalWire.parse(queryParams ?? {}),
    { translate: true, prependLocale: true }
  );
}

export function railsMandrillInboundEmailsPath(
  queryParams: GlobalQuery = {}
): string {
  return format(
    '/rails/action_mailbox/mandrill/inbound_emails',
    {},
    globalWire.parse(queryParams ?? {}),
    { translate: true, prependLocale: true }
  );
}

export function railsMandrillInboundHealthCheckPath(
  queryParams: GlobalQuery = {}
): string {
  return format(
    '/rails/action_mailbox/mandrill/inbound_emails',
    {},
    globalWire.parse(queryParams ?? {}),
    { translate: true, prependLocale: true }
  );
}

export function railsPostmarkInboundEmailsPath(
  queryParams: GlobalQuery = {}
): string {
  return format(
    '/rails/action_mailbox/postmark/inbound_emails',
    {},
    globalWire.parse(queryParams ?? {}),
    { translate: true, prependLocale: true }
  );
}

export function railsRelayInboundEmailsPath(
  queryParams: GlobalQuery = {}
): string {
  return format(
    '/rails/action_mailbox/relay/inbound_emails',
    {},
    globalWire.parse(queryParams ?? {}),
    { translate: true, prependLocale: true }
  );
}

export function railsSendgridInboundEmailsPath(
  queryParams: GlobalQuery = {}
): string {
  return format(
    '/rails/action_mailbox/sendgrid/inbound_emails',
    {},
    globalWire.parse(queryParams ?? {}),
    { translate: true, prependLocale: true }
  );
}

export function reactivateStaffEventRequestPath(
  pathParams: { id: Param },
  queryParams: GlobalQuery = {}
): string {
  return format(
    '/staff/event_requests/:id/reactivate',
    pathParams,
    globalWire.parse(queryParams ?? {}),
    { translate: true, prependLocale: true }
  );
}

export function removeUserInvitationPath(
  queryParams: GlobalQuery = {}
): string {
  return format('/invitation/remove', {}, globalWire.parse(queryParams ?? {}), {
    translate: true,
    prependLocale: true
  });
}

export function reviewPath(
  pathParams: { id: Param },
  queryParams: GlobalQuery = {}
): string {
  return format(
    '/reviews/:id',
    pathParams,
    globalWire.parse(queryParams ?? {}),
    { translate: true, prependLocale: true }
  );
}

export function robotsPath(queryParams: GlobalQuery = {}): string {
  return format('/robots.txt', {}, globalWire.parse(queryParams ?? {}), {
    translate: false,
    prependLocale: false
  });
}

export function rootPath(queryParams: GlobalQuery = {}): string {
  return format('/', {}, globalWire.parse(queryParams ?? {}), {
    translate: true,
    prependLocale: true
  });
}

export function rubyEventStoreBrowserAppPath(
  queryParams: GlobalQuery = {}
): string {
  return format('/res', {}, globalWire.parse(queryParams ?? {}), {
    translate: true,
    prependLocale: true
  });
}

export function ruisrockLippujenVoittajaPath(
  queryParams: GlobalQuery = {}
): string {
  return format(
    '/ruisrock-lippujen-voittaja',
    {},
    globalWire.parse(queryParams ?? {}),
    { translate: true, prependLocale: true }
  );
}

const searchSuppliersQuery = () =>
  z.object({
    ...globalQuery,
    page: queryValue
  });

export type SearchSuppliersQuery = z.input<
  ReturnType<typeof searchSuppliersQuery>
>;

const searchSuppliersSegments = lazy(() =>
  z
    .object({ category: segment, subCategory: segment, location: segment })
    .transform(({ category, subCategory, location }) => ({
      category,
      sub_category: subCategory,
      location
    }))
);

export type SearchSuppliersSegments = z.input<
  ReturnType<typeof searchSuppliersSegments>
>;

export function searchSuppliersPath(
  pathParams: SearchSuppliersSegments,
  queryParams: SearchSuppliersQuery = {}
): string {
  return format(
    '/s(/:category)(/:sub_category)(/:location)',
    searchSuppliersSegments().parse(pathParams),
    globalWire.parse(queryParams ?? {}),
    { translate: true, prependLocale: true }
  );
}

export function sendCopyToAuthorPriceRequestPath(
  pathParams: { id: Param },
  queryParams: GlobalQuery = {}
): string {
  return format(
    '/price_requests/:id/send-copy',
    pathParams,
    globalWire.parse(queryParams ?? {}),
    { translate: true, prependLocale: true }
  );
}

export function sidekiqWebPath(queryParams: GlobalQuery = {}): string {
  return format('/sidekiq', {}, globalWire.parse(queryParams ?? {}), {
    translate: true,
    prependLocale: true
  });
}

const spamInquiriesStaffCommonSpamInquiriesQuery = () =>
  z.object({
    ...globalQuery,
    page: queryValue,
    tab: oneOf<'flagged' | 'confirmed' | 'not_spam'>()
  });

export type SpamInquiriesStaffCommonSpamInquiriesQuery = z.input<
  ReturnType<typeof spamInquiriesStaffCommonSpamInquiriesQuery>
>;

export function spamInquiriesStaffCommonSpamInquiriesPath(
  queryParams: SpamInquiriesStaffCommonSpamInquiriesQuery = {}
): string {
  return format(
    '/staff/spam_inquiries',
    {},
    globalWire.parse(queryParams ?? {}),
    { translate: true, prependLocale: true }
  );
}

export function splitDashboardPath(queryParams: GlobalQuery = {}): string {
  return format('/split', {}, globalWire.parse(queryParams ?? {}), {
    translate: true,
    prependLocale: true
  });
}

export function staffCateringPath(queryParams: GlobalQuery = {}): string {
  return format('/staff/catering', {}, globalWire.parse(queryParams ?? {}), {
    translate: true,
    prependLocale: true
  });
}

export function staffCateringLandingPagePath(
  pathParams: { id: Param },
  queryParams: GlobalQuery = {}
): string {
  return format(
    '/staff/catering_landing_pages/:id',
    pathParams,
    globalWire.parse(queryParams ?? {}),
    { translate: true, prependLocale: true }
  );
}

export function staffCateringLandingPagesPath(
  queryParams: GlobalQuery = {}
): string {
  return format(
    '/staff/catering_landing_pages',
    {},
    globalWire.parse(queryParams ?? {}),
    { translate: true, prependLocale: true }
  );
}

const staffCateringProfileTransferSegments = lazy(() =>
  z
    .object({ profileId: segment })
    .transform(({ profileId }) => ({ profile_id: profileId }))
);

export type StaffCateringProfileTransferSegments = z.input<
  ReturnType<typeof staffCateringProfileTransferSegments>
>;

export function staffCateringProfileTransferPath(
  pathParams: StaffCateringProfileTransferSegments,
  queryParams: GlobalQuery = {}
): string {
  return format(
    '/staff/catering/profiles/:profile_id/transfer',
    staffCateringProfileTransferSegments().parse(pathParams),
    globalWire.parse(queryParams ?? {}),
    { translate: true, prependLocale: true }
  );
}

export function staffCateringServiceAreaCitiesPath(
  queryParams: GlobalQuery = {}
): string {
  return format(
    '/staff/catering/service_area_cities',
    {},
    globalWire.parse(queryParams ?? {}),
    { translate: true, prependLocale: true }
  );
}

export function staffCateringServiceAreaCityPath(
  pathParams: { id: Param },
  queryParams: GlobalQuery = {}
): string {
  return format(
    '/staff/catering/service_area_cities/:id',
    pathParams,
    globalWire.parse(queryParams ?? {}),
    { translate: true, prependLocale: true }
  );
}

export function staffConversationAppAccessGroupSyncsPath(
  queryParams: GlobalQuery = {}
): string {
  return format(
    '/staff/conversation_app_access_group_syncs',
    {},
    globalWire.parse(queryParams ?? {}),
    { translate: true, prependLocale: true }
  );
}

export function staffCSVExportsPath(queryParams: GlobalQuery = {}): string {
  return format('/staff/csv_exports', {}, globalWire.parse(queryParams ?? {}), {
    translate: true,
    prependLocale: true
  });
}

const staffFeaturedVenuesQuery = () =>
  z.object({
    ...globalQuery,
    startMonth: month
  });

export type StaffFeaturedVenuesQuery = z.input<
  ReturnType<typeof staffFeaturedVenuesQuery>
>;

const staffFeaturedVenuesWire = lazy(() =>
  staffFeaturedVenuesQuery()
    .catchall(queryValue)
    .transform(({ startMonth, disableFeatures, ...query }) => ({
      start_month: startMonth,
      disable_features: disableFeatures,
      ...query
    }))
);

export function staffFeaturedVenuesPath(
  queryParams: StaffFeaturedVenuesQuery = {}
): string {
  return format(
    '/staff/featured_venues',
    {},
    staffFeaturedVenuesWire().parse(queryParams ?? {}),
    { translate: true, prependLocale: true }
  );
}

export function staffImagePath(
  pathParams: { id: Param },
  queryParams: GlobalQuery = {}
): string {
  return format(
    '/staff/images/:id',
    pathParams,
    globalWire.parse(queryParams ?? {}),
    { translate: true, prependLocale: true }
  );
}

const staffImagesQuery = () =>
  z.object({
    ...globalQuery,
    attachableId: queryValue,
    page: queryValue,
    style: queryValue
  });

export type StaffImagesQuery = z.input<ReturnType<typeof staffImagesQuery>>;

const staffImagesWire = lazy(() =>
  staffImagesQuery()
    .catchall(queryValue)
    .transform(({ attachableId, disableFeatures, ...query }) => ({
      attachable_id: attachableId,
      disable_features: disableFeatures,
      ...query
    }))
);

export function staffImagesPath(queryParams: StaffImagesQuery = {}): string {
  return format(
    '/staff/images',
    {},
    staffImagesWire().parse(queryParams ?? {}),
    { translate: true, prependLocale: true }
  );
}

export function staffInvoicePath(
  pathParams: { id: Param },
  queryParams: GlobalQuery = {}
): string {
  return format(
    '/staff/invoices/:id',
    pathParams,
    globalWire.parse(queryParams ?? {}),
    { translate: true, prependLocale: true }
  );
}

const staffInvoicesQuery = () =>
  z.object({
    ...globalQuery,
    page: queryValue
  });

export type StaffInvoicesQuery = z.input<ReturnType<typeof staffInvoicesQuery>>;

export function staffInvoicesPath(
  queryParams: StaffInvoicesQuery = {}
): string {
  return format('/staff/invoices', {}, globalWire.parse(queryParams ?? {}), {
    translate: true,
    prependLocale: true
  });
}

export function staffLandingPagePath(
  pathParams: { id: Param },
  queryParams: GlobalQuery = {}
): string {
  return format(
    '/staff/landing_pages/:id',
    pathParams,
    globalWire.parse(queryParams ?? {}),
    { translate: true, prependLocale: true }
  );
}

export function staffLandingPagesPath(queryParams: GlobalQuery = {}): string {
  return format(
    '/staff/landing_pages',
    {},
    globalWire.parse(queryParams ?? {}),
    { translate: true, prependLocale: true }
  );
}

const staffMetricsQuery = () =>
  z.object({
    ...globalQuery,
    byEventAt: queryValue,
    from: month,
    noFuture: queryValue,
    offsetByDays: queryValue,
    to: month,
    whereEventAt: month
  });

export type StaffMetricsQuery = z.input<ReturnType<typeof staffMetricsQuery>>;

const staffMetricsWire = lazy(() =>
  staffMetricsQuery()
    .catchall(queryValue)
    .transform(
      ({
        byEventAt,
        noFuture,
        offsetByDays,
        whereEventAt,
        disableFeatures,
        ...query
      }) => ({
        by_event_at: byEventAt,
        no_future: noFuture,
        offset_by_days: offsetByDays,
        where_event_at: whereEventAt,
        disable_features: disableFeatures,
        ...query
      })
    )
);

export function staffMetricsPath(queryParams: StaffMetricsQuery = {}): string {
  return format(
    '/staff/metrics',
    {},
    staffMetricsWire().parse(queryParams ?? {}),
    { translate: true, prependLocale: true }
  );
}

export function staffOrganizationPath(
  pathParams: { id: Param },
  queryParams: GlobalQuery = {}
): string {
  return format(
    '/staff/organizations/:id',
    pathParams,
    globalWire.parse(queryParams ?? {}),
    { translate: true, prependLocale: true }
  );
}

const staffOrganizationContractSegments = lazy(() =>
  z
    .object({ organizationId: segment, id: segment })
    .transform(({ organizationId, id }) => ({
      organization_id: organizationId,
      id
    }))
);

export type StaffOrganizationContractSegments = z.input<
  ReturnType<typeof staffOrganizationContractSegments>
>;

export function staffOrganizationContractPath(
  pathParams: StaffOrganizationContractSegments,
  queryParams: GlobalQuery = {}
): string {
  return format(
    '/staff/organizations/:organization_id/contracts/:id',
    staffOrganizationContractSegments().parse(pathParams),
    globalWire.parse(queryParams ?? {}),
    { translate: true, prependLocale: true }
  );
}

const staffOrganizationContractsSegments = lazy(() =>
  z
    .object({ organizationId: segment })
    .transform(({ organizationId }) => ({ organization_id: organizationId }))
);

export type StaffOrganizationContractsSegments = z.input<
  ReturnType<typeof staffOrganizationContractsSegments>
>;

export function staffOrganizationContractsPath(
  pathParams: StaffOrganizationContractsSegments,
  queryParams: GlobalQuery = {}
): string {
  return format(
    '/staff/organizations/:organization_id/contracts',
    staffOrganizationContractsSegments().parse(pathParams),
    globalWire.parse(queryParams ?? {}),
    { translate: true, prependLocale: true }
  );
}

export function staffOrganizationLinkPath(
  pathParams: { id: Param },
  queryParams: GlobalQuery = {}
): string {
  return format(
    '/staff/organization_links/:id',
    pathParams,
    globalWire.parse(queryParams ?? {}),
    { translate: true, prependLocale: true }
  );
}

export function staffOrganizationLinksPath(
  queryParams: GlobalQuery = {}
): string {
  return format(
    '/staff/organization_links',
    {},
    globalWire.parse(queryParams ?? {}),
    { translate: true, prependLocale: true }
  );
}

const staffOrganizationPipedriveStatusSegments = lazy(() =>
  z
    .object({ organizationId: segment })
    .transform(({ organizationId }) => ({ organization_id: organizationId }))
);

export type StaffOrganizationPipedriveStatusSegments = z.input<
  ReturnType<typeof staffOrganizationPipedriveStatusSegments>
>;

export function staffOrganizationPipedriveStatusPath(
  pathParams: StaffOrganizationPipedriveStatusSegments,
  queryParams: GlobalQuery = {}
): string {
  return format(
    '/staff/organizations/:organization_id/pipedrive_status',
    staffOrganizationPipedriveStatusSegments().parse(pathParams),
    globalWire.parse(queryParams ?? {}),
    { translate: true, prependLocale: true }
  );
}

const staffOrganizationProfileAnnouncementsSegments = lazy(() =>
  z
    .object({ organizationId: segment })
    .transform(({ organizationId }) => ({ organization_id: organizationId }))
);

export type StaffOrganizationProfileAnnouncementsSegments = z.input<
  ReturnType<typeof staffOrganizationProfileAnnouncementsSegments>
>;

export function staffOrganizationProfileAnnouncementsPath(
  pathParams: StaffOrganizationProfileAnnouncementsSegments,
  queryParams: GlobalQuery = {}
): string {
  return format(
    '/staff/organizations/:organization_id/profile_announcements',
    staffOrganizationProfileAnnouncementsSegments().parse(pathParams),
    globalWire.parse(queryParams ?? {}),
    { translate: true, prependLocale: true }
  );
}

const staffOrganizationScheduledFeeSegments = lazy(() =>
  z
    .object({ organizationId: segment, id: segment })
    .transform(({ organizationId, id }) => ({
      organization_id: organizationId,
      id
    }))
);

export type StaffOrganizationScheduledFeeSegments = z.input<
  ReturnType<typeof staffOrganizationScheduledFeeSegments>
>;

export function staffOrganizationScheduledFeePath(
  pathParams: StaffOrganizationScheduledFeeSegments,
  queryParams: GlobalQuery = {}
): string {
  return format(
    '/staff/organizations/:organization_id/scheduled_fees/:id',
    staffOrganizationScheduledFeeSegments().parse(pathParams),
    globalWire.parse(queryParams ?? {}),
    { translate: true, prependLocale: true }
  );
}

const staffOrganizationScheduledFeesQuery = () =>
  z.object({
    ...globalQuery,
    page: queryValue
  });

export type StaffOrganizationScheduledFeesQuery = z.input<
  ReturnType<typeof staffOrganizationScheduledFeesQuery>
>;

const staffOrganizationScheduledFeesSegments = lazy(() =>
  z
    .object({ organizationId: segment })
    .transform(({ organizationId }) => ({ organization_id: organizationId }))
);

export type StaffOrganizationScheduledFeesSegments = z.input<
  ReturnType<typeof staffOrganizationScheduledFeesSegments>
>;

export function staffOrganizationScheduledFeesPath(
  pathParams: StaffOrganizationScheduledFeesSegments,
  queryParams: StaffOrganizationScheduledFeesQuery = {}
): string {
  return format(
    '/staff/organizations/:organization_id/scheduled_fees',
    staffOrganizationScheduledFeesSegments().parse(pathParams),
    globalWire.parse(queryParams ?? {}),
    { translate: true, prependLocale: true }
  );
}

const staffOrganizationServiceFeeSegments = lazy(() =>
  z
    .object({ organizationId: segment, id: segment })
    .transform(({ organizationId, id }) => ({
      organization_id: organizationId,
      id
    }))
);

export type StaffOrganizationServiceFeeSegments = z.input<
  ReturnType<typeof staffOrganizationServiceFeeSegments>
>;

export function staffOrganizationServiceFeePath(
  pathParams: StaffOrganizationServiceFeeSegments,
  queryParams: GlobalQuery = {}
): string {
  return format(
    '/staff/organizations/:organization_id/service_fees/:id',
    staffOrganizationServiceFeeSegments().parse(pathParams),
    globalWire.parse(queryParams ?? {}),
    { translate: true, prependLocale: true }
  );
}

const staffOrganizationServiceFeesQuery = () =>
  z.object({
    ...globalQuery,
    page: queryValue
  });

export type StaffOrganizationServiceFeesQuery = z.input<
  ReturnType<typeof staffOrganizationServiceFeesQuery>
>;

const staffOrganizationServiceFeesSegments = lazy(() =>
  z
    .object({ organizationId: segment })
    .transform(({ organizationId }) => ({ organization_id: organizationId }))
);

export type StaffOrganizationServiceFeesSegments = z.input<
  ReturnType<typeof staffOrganizationServiceFeesSegments>
>;

export function staffOrganizationServiceFeesPath(
  pathParams: StaffOrganizationServiceFeesSegments,
  queryParams: StaffOrganizationServiceFeesQuery = {}
): string {
  return format(
    '/staff/organizations/:organization_id/service_fees',
    staffOrganizationServiceFeesSegments().parse(pathParams),
    globalWire.parse(queryParams ?? {}),
    { translate: true, prependLocale: true }
  );
}

export function staffOrganizationsPath(queryParams: GlobalQuery = {}): string {
  return format(
    '/staff/organizations',
    {},
    globalWire.parse(queryParams ?? {}),
    { translate: true, prependLocale: true }
  );
}

const staffPriceRequestsQuery = () =>
  z.object({
    ...globalQuery,
    contractType: queryValue,
    direction: oneOf<'asc' | 'desc'>(),
    hasResponded: queryValue,
    organizationId: queryValue,
    page: queryValue,
    sort: queryValue,
    venueId: queryValue
  });

export type StaffPriceRequestsQuery = z.input<
  ReturnType<typeof staffPriceRequestsQuery>
>;

const staffPriceRequestsWire = lazy(() =>
  staffPriceRequestsQuery()
    .catchall(queryValue)
    .transform(
      ({
        contractType,
        hasResponded,
        organizationId,
        venueId,
        disableFeatures,
        ...query
      }) => ({
        contract_type: contractType,
        has_responded: hasResponded,
        organization_id: organizationId,
        venue_id: venueId,
        disable_features: disableFeatures,
        ...query
      })
    )
);

export function staffPriceRequestsPath(
  queryParams: StaffPriceRequestsQuery = {}
): string {
  return format(
    '/staff/price_requests',
    {},
    staffPriceRequestsWire().parse(queryParams ?? {}),
    { translate: true, prependLocale: true }
  );
}

export function staffProspectsPath(queryParams: GlobalQuery = {}): string {
  return format('/staff/prospects', {}, globalWire.parse(queryParams ?? {}), {
    translate: true,
    prependLocale: true
  });
}

export function staffRecurringSalesDashboardIndexPath(
  queryParams: GlobalQuery = {}
): string {
  return format(
    '/staff/recurring_sales_dashboard',
    {},
    globalWire.parse(queryParams ?? {}),
    { translate: true, prependLocale: true }
  );
}

export function staffReferralCookiePath(queryParams: GlobalQuery = {}): string {
  return format(
    '/staff/referral_cookie',
    {},
    globalWire.parse(queryParams ?? {}),
    { translate: true, prependLocale: true }
  );
}

export function staffReferralReportingGeneratedReportPath(
  pathParams: { id: Param },
  queryParams: GlobalQuery = {}
): string {
  return format(
    '/staff/referral_reporting/generated_reports/:id',
    pathParams,
    globalWire.parse(queryParams ?? {}),
    { translate: true, prependLocale: true }
  );
}

export function staffReferralReportingGeneratedReportsPath(
  queryParams: GlobalQuery = {}
): string {
  return format(
    '/staff/referral_reporting/generated_reports',
    {},
    globalWire.parse(queryParams ?? {}),
    { translate: true, prependLocale: true }
  );
}

export function staffReferralReportingReportPath(
  pathParams: { id: Param },
  queryParams: GlobalQuery = {}
): string {
  return format(
    '/staff/referral_reporting/reports/:id',
    pathParams,
    globalWire.parse(queryParams ?? {}),
    { translate: true, prependLocale: true }
  );
}

const staffReferralReportingReportsQuery = () =>
  z.object({
    ...globalQuery,
    rs: queryValue
  });

export type StaffReferralReportingReportsQuery = z.input<
  ReturnType<typeof staffReferralReportingReportsQuery>
>;

export function staffReferralReportingReportsPath(
  queryParams: StaffReferralReportingReportsQuery = {}
): string {
  return format(
    '/staff/referral_reporting/reports',
    {},
    globalWire.parse(queryParams ?? {}),
    { translate: true, prependLocale: true }
  );
}

const staffReferralReportingRootQuery = () =>
  z.object({
    ...globalQuery,
    rs: queryValue
  });

export type StaffReferralReportingRootQuery = z.input<
  ReturnType<typeof staffReferralReportingRootQuery>
>;

export function staffReferralReportingRootPath(
  queryParams: StaffReferralReportingRootQuery = {}
): string {
  return format(
    '/staff/referral_reporting',
    {},
    globalWire.parse(queryParams ?? {}),
    { translate: true, prependLocale: true }
  );
}

export function staffResendMessagePath(
  pathParams: { id: Param },
  queryParams: GlobalQuery = {}
): string {
  return format(
    '/staff/resend_messages/:id',
    pathParams,
    globalWire.parse(queryParams ?? {}),
    { translate: true, prependLocale: true }
  );
}

export function staffSearchSupplierSearchExcludedProfilePath(
  pathParams: { id: Param },
  queryParams: GlobalQuery = {}
): string {
  return format(
    '/staff/supplier_search_excluded_profiles/:id',
    pathParams,
    globalWire.parse(queryParams ?? {}),
    { translate: true, prependLocale: true }
  );
}

export function staffSearchSupplierSearchTopLevelCategoriesPath(
  queryParams: GlobalQuery = {}
): string {
  return format(
    '/staff/supplier_search_top_level_categories',
    {},
    globalWire.parse(queryParams ?? {}),
    { translate: true, prependLocale: true }
  );
}

export function staffSearchSupplierSearchTopLevelCategoryPath(
  pathParams: { id: Param },
  queryParams: GlobalQuery = {}
): string {
  return format(
    '/staff/supplier_search_top_level_categories/:id',
    pathParams,
    globalWire.parse(queryParams ?? {}),
    { translate: true, prependLocale: true }
  );
}

const staffSearchSupplierSearchTopLevelCategorySubCategoriesSegments = lazy(
  () =>
    z
      .object({ supplierSearchTopLevelCategoryId: segment })
      .transform(({ supplierSearchTopLevelCategoryId }) => ({
        supplier_search_top_level_category_id: supplierSearchTopLevelCategoryId
      }))
);

export type StaffSearchSupplierSearchTopLevelCategorySubCategoriesSegments =
  z.input<
    ReturnType<
      typeof staffSearchSupplierSearchTopLevelCategorySubCategoriesSegments
    >
  >;

export function staffSearchSupplierSearchTopLevelCategorySubCategoriesPath(
  pathParams: StaffSearchSupplierSearchTopLevelCategorySubCategoriesSegments,
  queryParams: GlobalQuery = {}
): string {
  return format(
    '/staff/supplier_search_top_level_categories/:supplier_search_top_level_category_id/sub_categories',
    staffSearchSupplierSearchTopLevelCategorySubCategoriesSegments().parse(
      pathParams
    ),
    globalWire.parse(queryParams ?? {}),
    { translate: true, prependLocale: true }
  );
}

const staffSearchSupplierSearchTopLevelCategorySubCategorySegments = lazy(() =>
  z
    .object({ supplierSearchTopLevelCategoryId: segment, id: segment })
    .transform(({ supplierSearchTopLevelCategoryId, id }) => ({
      supplier_search_top_level_category_id: supplierSearchTopLevelCategoryId,
      id
    }))
);

export type StaffSearchSupplierSearchTopLevelCategorySubCategorySegments =
  z.input<
    ReturnType<
      typeof staffSearchSupplierSearchTopLevelCategorySubCategorySegments
    >
  >;

export function staffSearchSupplierSearchTopLevelCategorySubCategoryPath(
  pathParams: StaffSearchSupplierSearchTopLevelCategorySubCategorySegments,
  queryParams: GlobalQuery = {}
): string {
  return format(
    '/staff/supplier_search_top_level_categories/:supplier_search_top_level_category_id/sub_categories/:id',
    staffSearchSupplierSearchTopLevelCategorySubCategorySegments().parse(
      pathParams
    ),
    globalWire.parse(queryParams ?? {}),
    { translate: true, prependLocale: true }
  );
}

export function staffSiteManagementPath(queryParams: GlobalQuery = {}): string {
  return format(
    '/staff/site_management',
    {},
    globalWire.parse(queryParams ?? {}),
    { translate: true, prependLocale: true }
  );
}

export function staffSiteManagementEventTypePath(
  pathParams: { id: Param },
  queryParams: GlobalQuery = {}
): string {
  return format(
    '/staff/site_management/event_types/:id',
    pathParams,
    globalWire.parse(queryParams ?? {}),
    { translate: true, prependLocale: true }
  );
}

export function staffSiteManagementEventTypesPath(
  queryParams: GlobalQuery = {}
): string {
  return format(
    '/staff/site_management/event_types',
    {},
    globalWire.parse(queryParams ?? {}),
    { translate: true, prependLocale: true }
  );
}

export function staffSiteManagementLocationGeoShapePath(
  pathParams: { id: Param },
  queryParams: GlobalQuery = {}
): string {
  return format(
    '/staff/site_management/location_geo_shapes/:id',
    pathParams,
    globalWire.parse(queryParams ?? {}),
    { translate: true, prependLocale: true }
  );
}

export function staffSiteManagementLocationGeoShapesPath(
  queryParams: GlobalQuery = {}
): string {
  return format(
    '/staff/site_management/location_geo_shapes',
    {},
    globalWire.parse(queryParams ?? {}),
    { translate: true, prependLocale: true }
  );
}

export function staffSiteManagementVenueServicePath(
  pathParams: { id: Param },
  queryParams: GlobalQuery = {}
): string {
  return format(
    '/staff/site_management/venue_services/:id',
    pathParams,
    globalWire.parse(queryParams ?? {}),
    { translate: true, prependLocale: true }
  );
}

export function staffSiteManagementVenueServicesPath(
  queryParams: GlobalQuery = {}
): string {
  return format(
    '/staff/site_management/venue_services',
    {},
    globalWire.parse(queryParams ?? {}),
    { translate: true, prependLocale: true }
  );
}

export function staffSiteManagementVenueTypePath(
  pathParams: { id: Param },
  queryParams: GlobalQuery = {}
): string {
  return format(
    '/staff/site_management/venue_types/:id',
    pathParams,
    globalWire.parse(queryParams ?? {}),
    { translate: true, prependLocale: true }
  );
}

export function staffSiteManagementVenueTypesPath(
  queryParams: GlobalQuery = {}
): string {
  return format(
    '/staff/site_management/venue_types',
    {},
    globalWire.parse(queryParams ?? {}),
    { translate: true, prependLocale: true }
  );
}

export function staffSpamReviewPath(
  pathParams: { id: Param },
  queryParams: GlobalQuery = {}
): string {
  return format(
    '/staff/spam_reviews/:id',
    pathParams,
    globalWire.parse(queryParams ?? {}),
    { translate: true, prependLocale: true }
  );
}

const staffSpamReviewsQuery = () =>
  z.object({
    ...globalQuery,
    page: queryValue
  });

export type StaffSpamReviewsQuery = z.input<
  ReturnType<typeof staffSpamReviewsQuery>
>;

export function staffSpamReviewsPath(
  queryParams: StaffSpamReviewsQuery = {}
): string {
  return format(
    '/staff/spam_reviews',
    {},
    globalWire.parse(queryParams ?? {}),
    { translate: true, prependLocale: true }
  );
}

export function staffSpamReviewsRecoverPath(
  queryParams: GlobalQuery = {}
): string {
  return format(
    '/staff/spam_reviews/recover',
    {},
    globalWire.parse(queryParams ?? {}),
    { translate: true, prependLocale: true }
  );
}

export function staffUserPath(
  pathParams: { id: Param },
  queryParams: GlobalQuery = {}
): string {
  return format(
    '/staff/users/:id',
    pathParams,
    globalWire.parse(queryParams ?? {}),
    { translate: true, prependLocale: true }
  );
}

const staffUsersQuery = () =>
  z.object({
    ...globalQuery,
    email: queryValue,
    page: queryValue,
    role: queryValue
  });

export type StaffUsersQuery = z.input<ReturnType<typeof staffUsersQuery>>;

export function staffUsersPath(queryParams: StaffUsersQuery = {}): string {
  return format('/staff/users', {}, globalWire.parse(queryParams ?? {}), {
    translate: true,
    prependLocale: true
  });
}

export function staffVenuePath(
  pathParams: { id: Param },
  queryParams: GlobalQuery = {}
): string {
  return format(
    '/staff/venues/:id',
    pathParams,
    globalWire.parse(queryParams ?? {}),
    { translate: true, prependLocale: true }
  );
}

const staffVenueFeaturedEventTypesSegments = lazy(() =>
  z
    .object({ venueId: segment })
    .transform(({ venueId }) => ({ venue_id: venueId }))
);

export type StaffVenueFeaturedEventTypesSegments = z.input<
  ReturnType<typeof staffVenueFeaturedEventTypesSegments>
>;

export function staffVenueFeaturedEventTypesPath(
  pathParams: StaffVenueFeaturedEventTypesSegments,
  queryParams: GlobalQuery = {}
): string {
  return format(
    '/staff/venues/:venue_id/featured_event_types',
    staffVenueFeaturedEventTypesSegments().parse(pathParams),
    globalWire.parse(queryParams ?? {}),
    { translate: true, prependLocale: true }
  );
}

const staffVenueRelevanceScoresSegments = lazy(() =>
  z
    .object({ venueId: segment })
    .transform(({ venueId }) => ({ venue_id: venueId }))
);

export type StaffVenueRelevanceScoresSegments = z.input<
  ReturnType<typeof staffVenueRelevanceScoresSegments>
>;

export function staffVenueRelevanceScoresPath(
  pathParams: StaffVenueRelevanceScoresSegments,
  queryParams: GlobalQuery = {}
): string {
  return format(
    '/staff/venues/:venue_id/relevance_scores',
    staffVenueRelevanceScoresSegments().parse(pathParams),
    globalWire.parse(queryParams ?? {}),
    { translate: true, prependLocale: true }
  );
}

export function staffVenuesPath(queryParams: GlobalQuery = {}): string {
  return format('/staff/venues', {}, globalWire.parse(queryParams ?? {}), {
    translate: true,
    prependLocale: true
  });
}

export function staffWorkbenchesPath(queryParams: GlobalQuery = {}): string {
  return format('/staff/workbenches', {}, globalWire.parse(queryParams ?? {}), {
    translate: true,
    prependLocale: true
  });
}

export function staffWorkbenchesRelevanceVisualizationPath(
  queryParams: GlobalQuery = {}
): string {
  return format(
    '/staff/workbenches/relevance_visualization',
    {},
    globalWire.parse(queryParams ?? {}),
    { translate: true, prependLocale: true }
  );
}

export function staffWorkbenchesSimilarityVisualizationPath(
  queryParams: GlobalQuery = {}
): string {
  return format(
    '/staff/workbenches/similarity_visualization',
    {},
    globalWire.parse(queryParams ?? {}),
    { translate: true, prependLocale: true }
  );
}

export function styleguidePath(queryParams: GlobalQuery = {}): string {
  return format('/styleguide', {}, globalWire.parse(queryParams ?? {}), {
    translate: true,
    prependLocale: true
  });
}

const succeededOutcomesCommonOutcomesEventCandidateSegments = lazy(() =>
  z
    .object({ token: segment, outcomeId: segment })
    .transform(({ token, outcomeId }) => ({ token, outcome_id: outcomeId }))
);

export type SucceededOutcomesCommonOutcomesEventCandidateSegments = z.input<
  ReturnType<typeof succeededOutcomesCommonOutcomesEventCandidateSegments>
>;

export function succeededOutcomesCommonOutcomesEventCandidatePath(
  pathParams: SucceededOutcomesCommonOutcomesEventCandidateSegments,
  queryParams: GlobalQuery = {}
): string {
  return format(
    '/event_feedback/:token/succeeded/:outcome_id',
    succeededOutcomesCommonOutcomesEventCandidateSegments().parse(pathParams),
    globalWire.parse(queryParams ?? {}),
    { translate: true, prependLocale: true }
  );
}

export function swapStaffSearchSupplierSearchTopLevelCategoriesPath(
  queryParams: GlobalQuery = {}
): string {
  return format(
    '/staff/supplier_search_top_level_categories/swap',
    {},
    globalWire.parse(queryParams ?? {}),
    { translate: true, prependLocale: true }
  );
}

const swapStaffSearchSupplierSearchTopLevelCategorySubCategoriesSegments = lazy(
  () =>
    z
      .object({ supplierSearchTopLevelCategoryId: segment })
      .transform(({ supplierSearchTopLevelCategoryId }) => ({
        supplier_search_top_level_category_id: supplierSearchTopLevelCategoryId
      }))
);

export type SwapStaffSearchSupplierSearchTopLevelCategorySubCategoriesSegments =
  z.input<
    ReturnType<
      typeof swapStaffSearchSupplierSearchTopLevelCategorySubCategoriesSegments
    >
  >;

export function swapStaffSearchSupplierSearchTopLevelCategorySubCategoriesPath(
  pathParams: SwapStaffSearchSupplierSearchTopLevelCategorySubCategoriesSegments,
  queryParams: GlobalQuery = {}
): string {
  return format(
    '/staff/supplier_search_top_level_categories/:supplier_search_top_level_category_id/sub_categories/swap',
    swapStaffSearchSupplierSearchTopLevelCategorySubCategoriesSegments().parse(
      pathParams
    ),
    globalWire.parse(queryParams ?? {}),
    { translate: true, prependLocale: true }
  );
}

export function tentativeOutcomesCommonOutcomesEventCandidatePath(
  pathParams: { token: Param },
  queryParams: GlobalQuery = {}
): string {
  return format(
    '/event_feedback/:token/tentative',
    pathParams,
    globalWire.parse(queryParams ?? {}),
    { translate: true, prependLocale: true }
  );
}

export function termsOfUsePath(queryParams: GlobalQuery = {}): string {
  return format('/terms-of-use', {}, globalWire.parse(queryParams ?? {}), {
    translate: true,
    prependLocale: true
  });
}

export function thanksPriceRequestsPath(queryParams: GlobalQuery = {}): string {
  return format(
    '/price_requests/thanks',
    {},
    globalWire.parse(queryParams ?? {}),
    { translate: true, prependLocale: true }
  );
}

export function toggleBookingInquiryStaffVenuePath(
  pathParams: { id: Param },
  queryParams: GlobalQuery = {}
): string {
  return format(
    '/staff/venues/:id/toggle_booking_inquiry',
    pathParams,
    globalWire.parse(queryParams ?? {}),
    { translate: true, prependLocale: true }
  );
}

export function transferStaffOrganizationPath(
  pathParams: { id: Param },
  queryParams: GlobalQuery = {}
): string {
  return format(
    '/staff/organizations/:id/transfer',
    pathParams,
    globalWire.parse(queryParams ?? {}),
    { translate: true, prependLocale: true }
  );
}

export function turkuPath(queryParams: GlobalQuery = {}): string {
  return format('/turku', {}, globalWire.parse(queryParams ?? {}), {
    translate: true,
    prependLocale: true
  });
}

export function unknownOutcomesCommonOutcomesEventCandidatePath(
  pathParams: { token: Param },
  queryParams: GlobalQuery = {}
): string {
  return format(
    '/event_feedback/:token/unknown',
    pathParams,
    globalWire.parse(queryParams ?? {}),
    { translate: true, prependLocale: true }
  );
}

export function updateNoteListPath(
  pathParams: { id: Param },
  queryParams: GlobalQuery = {}
): string {
  return format(
    '/lists/:id/update_note',
    pathParams,
    globalWire.parse(queryParams ?? {}),
    { translate: true, prependLocale: true }
  );
}

const userConfirmationQuery = () =>
  z.object({
    ...globalQuery,
    confirmationToken: queryValue
  });

export type UserConfirmationQuery = z.input<
  ReturnType<typeof userConfirmationQuery>
>;

const userConfirmationWire = lazy(() =>
  userConfirmationQuery()
    .catchall(queryValue)
    .transform(({ confirmationToken, disableFeatures, ...query }) => ({
      confirmation_token: confirmationToken,
      disable_features: disableFeatures,
      ...query
    }))
);

export function userConfirmationPath(
  queryParams: UserConfirmationQuery = {}
): string {
  return format(
    '/confirmation',
    {},
    userConfirmationWire().parse(queryParams ?? {}),
    { translate: true, prependLocale: true }
  );
}

export function userInvitationPath(queryParams: GlobalQuery = {}): string {
  return format('/invitation', {}, globalWire.parse(queryParams ?? {}), {
    translate: true,
    prependLocale: true
  });
}

export function userPasswordPath(queryParams: GlobalQuery = {}): string {
  return format('/password', {}, globalWire.parse(queryParams ?? {}), {
    translate: true,
    prependLocale: true
  });
}

export function userRegistrationPath(queryParams: GlobalQuery = {}): string {
  return format('/', {}, globalWire.parse(queryParams ?? {}), {
    translate: true,
    prependLocale: true
  });
}

export function userSessionPath(queryParams: GlobalQuery = {}): string {
  return format('/login', {}, globalWire.parse(queryParams ?? {}), {
    translate: true,
    prependLocale: true
  });
}

const venueReviewsSegments = lazy(() =>
  z
    .object({ venueId: segment })
    .transform(({ venueId }) => ({ venue_id: venueId }))
);

export type VenueReviewsSegments = z.input<
  ReturnType<typeof venueReviewsSegments>
>;

export function venueReviewsPath(
  pathParams: VenueReviewsSegments,
  queryParams: GlobalQuery = {}
): string {
  return format(
    '/venues/:venue_id/reviews',
    venueReviewsSegments().parse(pathParams),
    globalWire.parse(queryParams ?? {}),
    { translate: true, prependLocale: true }
  );
}

const venuesQuery = () =>
  z.object({
    ...globalQuery,
    availability: queryValue,
    et: queryValue,
    location: queryValue,
    ne: queryValue,
    page: queryValue,
    people: queryValue,
    peopleFilter: queryValue,
    sort: queryValue,
    sw: queryValue,
    title: queryValue,
    vs: queryValue,
    vt: queryValue
  });

export type VenuesQuery = z.input<ReturnType<typeof venuesQuery>>;

const venuesWire = lazy(() =>
  venuesQuery()
    .catchall(queryValue)
    .transform(({ peopleFilter, disableFeatures, ...query }) => ({
      people_filter: peopleFilter,
      disable_features: disableFeatures,
      ...query
    }))
);

export function venuesPath(queryParams: VenuesQuery = {}): string {
  return format('/venues', {}, venuesWire().parse(queryParams ?? {}), {
    translate: true,
    prependLocale: true
  });
}
